'use client'

import { dayStarts, isShootDayLevel } from '@repo/db/shoot-day'
import { priceShoot, type ShootMargin } from '@repo/db/shoot-price'
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Check,
  Globe,
  Home,
  Info,
  Lock,
  type LucideIcon,
  MapPin,
  Package,
  Sparkles,
  UserRound,
  Users,
} from 'lucide-react'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  type BookingSlot,
  ConsultationCalendar,
} from '@/components/molecules/consultation-calendar.client'
import { PageHeader } from '@/components/molecules/page-header'
import { type Choice, PEOPLE_CHOICES, PillGroup } from '@/components/molecules/pill-group.client'
import { TiltCard } from '@/components/molecules/tilt-card.client'

import { creatorHref } from '@/lib/creator'
import { CONTAINER, SCREEN_LOCK, SCREEN_TOP } from '@/lib/layout'
import { formatPlnMinor } from '@/lib/money'
import {
  type CityView,
  citiesFor,
  type SpecialistView,
  specialistIcon,
  specialistTone,
} from '@/lib/production'
import { PICKED_FILL, PICKED_RING, RADIUS } from '@/lib/surface'
import { ACCENT_TEXT, MICRO, STAT, SUBHEADING } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Production booking: pick a specialist, a city, then a preferred slot.
 *
 * Nothing here reserves anything — there is no booking backend in this project.
 * The picked slot is carried onward as a query param and the copy says
 * "preferowany termin", never "zarezerwowane". A calendar that implied a held
 * slot would have the visitor believe a booking exists that nobody made.
 *
 * The answers lead ONE way: into `/pl/kreator`, which turns them into an order
 * line and prices it. This page collects; it does not price, and it does not
 * own a basket. It used to offer "Wolę zapytać" beside that button — see the
 * note where that link used to be built for why the owner took it out.
 *
 * The catalogue arrives as props from the route, which reads it per request:
 * this used to be a hand-written array, so the panel had nothing to edit and
 * the page could not have been wrong about a price because nobody could change
 * one.
 */

const slotFormatter = new Intl.DateTimeFormat('pl-PL', { day: 'numeric', month: 'long' })

/**
 * What kind of shoot it is — the photographer's own list, with what each one is.
 *
 * Owner supplied all six with their descriptions and asked for two things: the
 * NAMES in the picker, and the descriptions somewhere a reader can get at them
 * — *„ale nie w panelu «wybierz szczegóły», dodajmy jakąś emotkę […] jak
 * informacje, które będzie pokazywać, co dana sesja daje"*.
 *
 * They are not in the panel because they do not fit: measured with a shoot role
 * and every question answered, that panel runs 528px of content in 530px of
 * room. A description under the pills is 54px the column does not have, and six
 * pills instead of four already cost a row.
 *
 * So they live one tap away, in `SessionGuide` below — which doubles as a
 * richer picker, because a list that explains six things and then makes you go
 * back to press one of them has wasted the trip.
 *
 * Icons rather than emoji, even though the owner said „emotka": every glyph on
 * this site is Lucide, and one row of Apple emoji among them reads as a
 * different site. Same job, same voice.
 *
 * Fixed answers rather than catalogue data, because there is no column behind
 * them — nothing in `offering` describes a session type, and a table for six
 * strings nobody prices off is a migration in search of a reason.
 */
const SESSION_TYPES = [
  {
    value: 'wizerunkowa',
    label: 'Wizerunkowa',
    Icon: Briefcase,
    blurb:
      'Profesjonalne zdjęcia dla przedsiębiorców, twórców, ekspertów i zespołów. Idealne na stronę internetową, LinkedIn, social media i do budowania marki osobistej.',
  },
  {
    value: 'portretowa',
    label: 'Portretowa',
    Icon: UserRound,
    blurb:
      'Naturalne i estetyczne zdjęcia jednej osoby, dopasowane do Twojego stylu. Sprawdzą się do portfolio, profilu zawodowego, social mediów albo po prostu dla siebie.',
  },
  {
    value: 'kobieca',
    label: 'Kobieca',
    Icon: Sparkles,
    blurb:
      'Sesja dla kobiet, które chcą poczuć się dobrze przed obiektywem i zobaczyć siebie w najlepszym wydaniu. Możemy stworzyć zarówno naturalne portrety, jak i bardziej modowe lub beauty ujęcia.',
  },
  {
    value: 'rodzinna',
    label: 'Rodzinna',
    Icon: Users,
    blurb:
      'Naturalne zdjęcia rodzin, par i dzieci, które zostają pamiątką na lata. Sesję możemy przygotować na konkretną okazję albo po prostu po to, żeby zatrzymać wspólny moment.',
  },
  {
    value: 'produktowa',
    label: 'Produktowa',
    Icon: Package,
    blurb:
      'Profesjonalne zdjęcia produktów do sklepów internetowych, reklam, katalogów i social mediów. Od prostych ujęć po bardziej dopracowane aranżacje dopasowane do marki.',
  },
  {
    value: 'nieruchomosci',
    label: 'Nieruchomości',
    Icon: Home,
    blurb:
      'Zdjęcia mieszkań, domów i lokali przeznaczonych na sprzedaż lub wynajem. Dobre kadry pomagają lepiej pokazać przestrzeń i wyróżnić ogłoszenie.',
  },
] as const satisfies readonly (Choice & { blurb: string; Icon: LucideIcon })[]

/**
 * …and what the OPERATOR is asked instead, in his own words.
 *
 * ── THE ANSWERS ARE NOT HERE ANY MORE ────────────────────────────────────
 * They were: six kinds typed into this file, beside a plan picker that sold his
 * day in hours. The owner retired the plans and made the KIND the thing being
 * bought — so the six live in `recording_type`, where the panel can price them
 * and rename them, and this file reads them like it reads the session tables.
 *
 * What stays is the glyph set. A kind carries the NAME of its icon, and this is
 * the closed list those names resolve against — the same arrangement
 * `SPECIALIST_ICONS` has, and for the same reason: an icon is code, not data.
 */
const OPERATOR_ROLE = 'operator'

/**
 * What a shoot still has to be told, in the words of the session it is.
 *
 * One question with one list of answers used to do for all of it, and it could
 * not. „3+" is the whole difference between a couple and a team of twelve. A
 * family reading „przed obiektywem" is being asked about their afternoon in the
 * language of a product shoot. And a flat has nobody in front of the lens at
 * all — it has a kind, a floor area, and the question of whether we photograph
 * the outside of it too. Owner supplied every list.
 *
 * So a session carries a LIST of questions rather than one, and that list is
 * what the price gate, the summary and the clearing rule all read. Everything
 * that counts, counts in RANGES, for the reason the first one did: the page
 * prices nothing off these. What they do is tell the photographer what the day
 * is — one face or twelve, a table of products or 200 m² of empty flat.
 */
type DetailQuestion = {
  /**
   * Where the answer is kept — and what it MEANS, across sessions: an answer
   * survives a change of session only where the new one asks the same `key`
   * and still offers that value. „2 osoby" carries from portretowa to
   * rodzinna; it does not become 2 m².
   */
  key: string
  label: string
  choices: readonly Choice[]
  columns: 2 | 3
  /**
   * Answers that end the path in „wycena indywidualna" instead of a figure.
   *
   * Owner, on the operator: *„więcej niż 3 lokalizacje powinny prowadzić do
   * indywidualnej wyceny"*. It is a property of the ANSWER and not of the
   * session, because one list can hold both kinds: three locations are priced
   * and four are talked about.
   */
  quoteValues?: readonly string[]
  /** What the price gate says is still missing — „uzupełnij …". */
  missing: string
  /** The same thing as a label, for the one line it folds to once answered. */
  noun: string
}

/**
 * ── WHAT THE OPERATOR IS ASKED, NOW THAT THE KIND IS GONE ────────────────
 * Two questions, and both of them move money — which is the whole difference
 * from the six kinds they replaced.
 *
 * The mic question is the owner's own wording and it is better than the
 * obvious one: *„najlepiej zrobić to na zasadzie ile osób będzie mówić do
 * kamery jednocześnie"*. The set carries two radio mics; a client knows how
 * many people they are putting in front of the camera and has no idea how many
 * transmitters that needs. So the page asks the thing they know and does the
 * conversion itself — see {@link MIC_PAIRS}.
 *
 * Lighting the same way: two lights and the RGB are in the set, and the only
 * thing on offer is a second pair. „Potrzebujesz drugiego kompletu" is a
 * yes/no about the shoot, not a line item about inventory.
 */
const MIC_KEY = 'mowiacy'
const LIGHTS_KEY = 'oswietlenie'

/** How many PAIRS of mics past the two in the set each answer needs. */
const MIC_PAIRS: Readonly<Record<string, number>> = { '1-2': 0, '3-4': 1, '5-6': 2 }

const OPERATOR_QUESTIONS: readonly DetailQuestion[] = [
  {
    key: MIC_KEY,
    label: 'Ile osób będzie mówić do kamery jednocześnie?',
    choices: [
      { value: '1-2', label: '1–2 osoby', short: '1–2 mówiące' },
      { value: '3-4', label: '3–4 osoby', short: '3–4 mówiące' },
      { value: '5-6', label: '5–6 osób', short: '5–6 mówiących' },
    ],
    columns: 3,
    missing: 'liczbę mówiących',
    noun: 'Mówiący',
  },
  {
    key: LIGHTS_KEY,
    label: 'Potrzebujesz drugiego kompletu świateł?',
    choices: [
      { value: 'nie', label: 'Nie, dwa wystarczą', short: '2 światła' },
      { value: 'tak', label: 'Tak, dołóżcie dwa', short: '4 światła' },
    ],
    columns: 2,
    missing: 'oświetlenie',
    noun: 'Oświetlenie',
  },
]

/** Anything in front of a lens, unless the session below asks its own way. */
const ON_SET_PEOPLE: DetailQuestion = {
  key: 'people',
  label: 'Ile osób przed obiektywem?',
  choices: [
    { value: '1', label: '1 osoba', short: '1 os.' },
    { value: '2', label: '2 osoby', short: '2 os.' },
    { value: '3-5', label: 'Od 3 do 5 osób', short: '3–5 os.' },
    { value: '6-10', label: 'Od 6 do 10 osób', short: '6–10 os.' },
    { value: '10-plus', label: 'Więcej niż 10 osób', short: '10+ os.' },
  ],
  columns: 2,
  missing: 'liczbę osób',
  noun: 'Liczba osób',
}

/** An hour at a table — one, two, or a small group. */
const MEETING_PEOPLE: DetailQuestion = {
  key: 'people',
  label: 'Ile osób na spotkaniu?',
  choices: PEOPLE_CHOICES,
  columns: 3,
  missing: 'liczbę osób',
  noun: 'Liczba osób',
}

/**
 * Sessions read off a TABLE instead of out of a range.
 *
 * ── THE TABLE IS NOT HERE ANY MORE ───────────────────────────────────────
 * It used to be: six tables typed into this file from the owner's screenshots,
 * beside a plan picker that read prices from the catalogue. Two sources for one
 * offer, and the page had no way to notice when they disagreed.
 *
 * The rows now arrive from `shoot_scope`, seeded from the owner's spreadsheet
 * and editable in `/pl/panel/oferta/stawki` — the same rows the price is
 * computed from, so what can be chosen and what it costs can no longer drift
 * apart. A count from one to ten (or fifteen products, or five floor areas),
 * and the three photo counts it offers.
 *
 * What STAYS here is the wording, because wording is not data: „Ile osób przed
 * obiektywem?" against „Ile osób weźmie udział w sesji?" is the same column of
 * the same table asked in two voices, and a database column holding a question
 * mark is a translation file with extra steps.
 *
 * And one more thing stays: the answers that have NO price by definition.
 * „Więcej niż 300 m²" is a real answer that ends in „wycena indywidualna" — it
 * is in no price table because there is no price. See {@link QUOTE_SCOPES}.
 */
type TableScopeRow = {
  /** The three counts this row is sold with, ascending. */
  photos: readonly number[]
  /** A head or product count, or `null` for a band that is not a count. */
  units: number | null
  /** Stated instead of derived — the property session's floor-area bands. */
  surchargeNetMinor: number | null
}

type TableRow = {
  label: string
  short: string
  /** What this answer is sold as — `null` for an answer with no price at all. */
  scope: TableScopeRow | null
  /** Still an answer, but one priced by hand. */
  quote?: boolean
}

type SessionTable = {
  /** Which answer the rows are keyed by — a count for most, an area for a flat. */
  key: string
  label: string
  /** „przy tylu osobach" — what a greyed-out plan says it is unavailable for. */
  because: string
  missing: string
  noun: string
  columns: 2 | 3
  rows: Record<string, TableRow>
}

/** The wording each table asks its own column in. Copy, not data. */
const TABLE_WORDS: Record<
  string,
  { key: string; label: string; because: string; missing: string; noun: string; columns: 2 | 3 }
> = {
  wizerunkowa: {
    key: 'people',
    label: 'Ile osób przed obiektywem?',
    because: 'przy tylu osobach',
    missing: 'liczbę osób',
    noun: 'Liczba osób',
    columns: 3,
  },
  portretowa: {
    key: 'people',
    label: 'Ile osób przed obiektywem?',
    because: 'przy tylu osobach',
    missing: 'liczbę osób',
    noun: 'Liczba osób',
    columns: 3,
  },
  kobieca: {
    key: 'people',
    label: 'Ile osób przed obiektywem?',
    because: 'przy tylu osobach',
    missing: 'liczbę osób',
    noun: 'Liczba osób',
    columns: 3,
  },
  rodzinna: {
    key: 'people',
    // Owner's wording, and the reason this map exists: a family „weźmie udział
    // w sesji", nobody in it is „przed obiektywem".
    label: 'Ile osób weźmie udział w sesji?',
    because: 'przy tylu osobach',
    missing: 'liczbę osób',
    noun: 'Liczba osób',
    columns: 3,
  },
  produktowa: {
    key: 'people',
    label: 'Ile produktów fotografujemy?',
    because: 'przy tylu produktach',
    missing: 'liczbę produktów',
    noun: 'Liczba produktów',
    columns: 3,
  },
  nieruchomosci: {
    key: 'powierzchnia',
    label: 'Jaka jest powierzchnia obiektu?',
    because: 'przy tej powierzchni',
    missing: 'powierzchnię',
    noun: 'Powierzchnia',
    columns: 3,
  },
}

/**
 * Answers that are offered but never priced.
 *
 * They cannot live in `shoot_scope`: every row there carries three photo counts
 * and this one has none — that is the whole point of it. Putting
 * it in the table with zeroes would make „wycena indywidualna" look like a
 * price of nothing to every reader of that table, including the next one to
 * write a query against it.
 *
 * So the line is drawn where it belongs: THE DATABASE OWNS EVERYTHING THAT HAS
 * A PRICE, and the page owns the answers that explicitly have none.
 */
const QUOTE_SCOPES: Record<string, readonly { value: string; label: string; short: string }[]> = {
  nieruchomosci: [{ value: '300-plus', label: 'Więcej niż 300 m²', short: 'ponad 300 m²' }],
}

/** The rates and the margin, as the page receives them. */
type ShootPricing = {
  rates: readonly {
    session: string
    baseNetMinor: number
    perPhotoNetMinor: number
    includedUnits: number
    extraUnitNetMinor: number
  }[]
  scopes: readonly {
    session: string
    value: string
    label: string
    shortLabel: string
    units: number | null
    surchargeNetMinor: number | null
    photos: [number, number, number]
  }[]
  /**
   * What the OPERATOR records, and what a day of it costs — `recording_type`.
   *
   * The same list this file used to hard-code as `RECORDING_TYPES`. It comes
   * from the database now for the reason the session tables do: the panel edits
   * it, and a copy in the page is a copy that goes stale the first time
   * somebody renames a kind.
   */
  recordingTypes: readonly {
    slug: string
    label: string
    blurb: string
    icon: string | null
    clientNetMinor: number | null
    performerNetMinor: number | null
    isActive: boolean
  }[]
  margin: ShootMargin
}

/**
 * The scope rows, folded back into one table per session.
 *
 * The database stores a row per (session, value) because that is what a price
 * is keyed by; the page asks by session. This is that fold, and it is the only
 * place the two shapes meet.
 */
function buildTables(pricing: ShootPricing | null): Record<string, SessionTable> {
  const tables: Record<string, SessionTable> = {}
  if (!pricing) return tables

  for (const scope of pricing.scopes) {
    const words = TABLE_WORDS[scope.session]
    if (!words) continue
    const table = (tables[scope.session] ??= { ...words, rows: {} })
    const row = (table.rows[scope.value] ??= {
      label: scope.label,
      short: scope.shortLabel,
      scope: null,
    })
    row.scope = {
      photos: scope.photos,
      units: scope.units,
      surchargeNetMinor: scope.surchargeNetMinor,
    }
  }

  // The quoted answers go LAST, after every priced row of that session: they
  // are the end of the list in the owner's table too, and „więcej niż" reads
  // as an answer only once the numbered ones have been read.
  for (const [session, quotes] of Object.entries(QUOTE_SCOPES)) {
    const table = tables[session]
    if (!table) continue
    for (const quote of quotes) {
      table.rows[quote.value] = {
        label: quote.label,
        short: quote.short,
        scope: null,
        quote: true,
      }
    }
  }

  return tables
}

/**
 * Where the photo count is kept — and deliberately NOT `zdjecia`.
 *
 * The property shoot already asks a question under that key („Wnętrza czy
 * zewnątrz?"), and two questions sharing one key answer each other: choosing
 * „wnętrza" would come back as a number of photos the moment somebody switched
 * session, and the clearing rule would think it had been answered.
 */
const PHOTO_COUNT = 'liczba-zdjec'

/** The row an answer set has landed on, if the session has a table at all. */
function rowFor(
  tables: Record<string, SessionTable>,
  sessionType: string | null,
  answers: Record<string, string>,
) {
  const table = sessionType ? tables[sessionType] : undefined
  if (!table) return undefined
  const value = answers[table.key]
  return value ? table.rows[value] : undefined
}

/**
 * Whether this configuration ends in a quote instead of a figure.
 *
 * Two sources, because there are two kinds of „we would have to talk about it":
 * a TABLE row with no price at all („Więcej niż 300 m²"), and an ANSWER that
 * says so on a session with no table (the operator's „Więcej niż 3
 * lokalizacje"). Both end the path the same way, so both are read here.
 */
function isQuoted(
  tables: Record<string, SessionTable>,
  sessionType: string | null,
  answers: Record<string, string>,
  questions: readonly DetailQuestion[],
) {
  if (rowFor(tables, sessionType, answers)?.quote) return true
  return questions.some((question) => {
    const answer = answers[question.key]
    return Boolean(answer && question.quoteValues?.includes(answer))
  })
}

/** A table's own question: its rows as answers, in the order they were read. */
function tableQuestionFor(table: SessionTable): DetailQuestion {
  return {
    key: table.key,
    label: table.label,
    choices: Object.entries(table.rows).map(([value, row]) => ({
      value,
      label: row.label,
      short: row.short,
    })),
    columns: table.columns,
    missing: table.missing,
    noun: table.noun,
  }
}

/**
 * „Ile zdjęć chcesz otrzymać?" — the last question of a tabled session, and the
 * only one on this page whose answers are computed rather than written down.
 *
 * It used to be asked after the PLAN, because the plan was half of its key:
 * five people gave „15, 20 lub 25" in three hours and „25, 40 lub 50" in eight.
 * With the halves retired the count answers it on its own.
 *
 * Returned even when the choices are empty, so the path keeps its length: the
 * step count would otherwise change the moment a count was pressed, which is
 * the counter-moving-under-the-reader this page already fixed once.
 */
function photoQuestionFor(
  tables: Record<string, SessionTable>,
  sessionType: string | null,
  answers: Record<string, string>,
  pricing: ShootPricing | null,
): DetailQuestion | null {
  const table = sessionType ? tables[sessionType] : undefined
  if (!table) return null
  const row = rowFor(tables, sessionType, answers)
  const packages = sessionType ? SHOOT_PACKAGES[sessionType] : undefined
  if (packages) return packageQuestionFor(packages, row?.scope, sessionType, pricing)
  return {
    key: PHOTO_COUNT,
    label: 'Ile zdjęć chcesz otrzymać?',
    choices: (row?.scope?.photos ?? []).map((count) => ({
      value: String(count),
      label: `${count} ${plural(count, 'zdjęcie', 'zdjęcia', 'zdjęć')}`,
      short: `${count} zdj.`,
    })),
    columns: 3,
    missing: 'liczbę zdjęć',
    noun: 'Liczba zdjęć',
  }
}

/**
 * A session sold as three named packages instead of three bare photo counts.
 *
 * Owner, 2026-10-14, after the market audit: the business session follows the
 * market's ladder — an hour and a handful of photos for a profile picture, two
 * hours for a website, three for a season of content. The three counts still
 * come from `shoot_scope` (low / mid / high) and the price from `priceShoot`, so
 * the checkout prices exactly what the card shows; this only names them and
 * says what each one holds.
 *
 * `hours` is the length for ONE person; each further person adds half an hour,
 * the same rule the counts follow (+3 / +4 / +5 photos a head).
 */
type ShootPackage = { name: string; hours: number; looks: string }

const SHOOT_PACKAGES: Partial<Record<string, readonly [ShootPackage, ShootPackage, ShootPackage]>> =
  {
    wizerunkowa: [
      { name: 'Profil', hours: 1, looks: '2 stylizacje' },
      { name: 'Wizerunek', hours: 2, looks: '3 stylizacje' },
      { name: 'Marka osobista', hours: 3, looks: 'stylizacje bez limitu' },
    ],
  }

/** „1 h", „2,5 h" — the session's length once the people in it are counted. */
function sessionLength(hours: number, people: number) {
  const total = hours + Math.max(0, people - 1) * 0.5
  return `${String(total).replace('.', ',')} h`
}

function packageQuestionFor(
  packages: readonly ShootPackage[],
  scope: TableScopeRow | null | undefined,
  sessionType: string | null,
  pricing: ShootPricing | null,
): DetailQuestion {
  const rate = pricing?.rates.find((r) => r.session === sessionType)
  const people = scope?.units ?? 1
  return {
    key: PHOTO_COUNT,
    label: 'Wybierz pakiet',
    choices: (scope?.photos ?? []).map((count, index) => {
      const pack = packages[index]
      const price =
        rate && scope && pricing
          ? priceShoot(rate, scope, count, pricing.margin).clientNetMinor
          : null
      return {
        value: String(count),
        label: pack?.name ?? `${count} zdj.`,
        short: pack ? `${pack.name} · ${count} zdj.` : `${count} zdj.`,
        detail: [
          `${count} ${plural(count, 'zdjęcie', 'zdjęcia', 'zdjęć')}`,
          pack ? sessionLength(pack.hours, people) : null,
          pack?.looks,
          price !== null ? formatPlnMinor(price) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      }
    }),
    columns: 3,
    missing: 'pakiet',
    noun: 'Pakiet',
  }
}

/**
 * Polish counts three ways, and these answers need all three of them.
 *
 * „1 zdjęcie", „24 zdjęcia", „25 zdjęć". The teens are the exception the rule
 * is famous for: 12, 13 and 14 take the many-form even though they end in 2, 3
 * and 4.
 */
function plural(n: number, one: string, few: string, many: string) {
  if (n === 1) return one
  const last = n % 10
  const teen = n % 100
  return last >= 2 && last <= 4 && (teen < 12 || teen > 14) ? few : many
}

/**
 * WHAT THE WHOLE PATH COMES TO, in net grosze — or `null` while it cannot say.
 *
 * The same `priceShoot` the panel previews with and the checkout will charge
 * by. `null` is returned rather than a guess whenever any of the three things
 * it needs is missing: a rate for this session, the row the count landed on,
 * and a photo count. The caller then falls back to the level's own catalogue
 * figure, which is what this page showed before the model existed.
 */
function priceOf(
  pricing: ShootPricing | null,
  tables: Record<string, SessionTable>,
  sessionType: string | null,
  answers: Record<string, string>,
) {
  if (!pricing || !sessionType) return null

  const rate = pricing.rates.find((r) => r.session === sessionType)
  if (!rate) return null

  const scope = rowFor(tables, sessionType, answers)?.scope
  if (!scope) return null

  const photos = Number(answers[PHOTO_COUNT])
  if (!Number.isFinite(photos) || photos <= 0) return null

  return priceShoot(rate, scope, photos, pricing.margin)
}

/**
 * Sessions that ask their own questions, keyed by the session type's slug.
 *
 * A family counts heads one at a time up to six and nobody in it is „przed
 * obiektywem" — they are taking part. A product shoot counts products. A
 * property is not counted at all. Everything not listed here falls back to
 * `ON_SET_PEOPLE`, so a new session type can never leave the column blank.
 */
const SESSION_QUESTIONS: Record<
  string,
  (table: DetailQuestion | null) => readonly DetailQuestion[]
> = {
  // A FUNCTION of the table's own question, because the table arrives from the
  // database and these lists are read at module load. Most sessions ARE their
  // table; the property shoot wraps it, and the operator's six ignore it.
  wizerunkowa: (table) => (table ? [table] : []),
  portretowa: (table) => (table ? [table] : []),
  kobieca: (table) => (table ? [table] : []),
  rodzinna: (table) => (table ? [table] : []),
  produktowa: (table) => (table ? [table] : []),
  // ── THE OPERATOR'S SIX, AND WHAT EACH ONE NEEDS TO KNOW ──────────────
  // Two or three questions per kind, from the owner's table. No price tables
  // behind them yet — the owner has priced photography and not recording — so
  // they ask, the plan follows, and the price is the plan's.
  //
  // The head count is `people` in every one of them ON PURPOSE: somebody who
  // answers „2 osoby" for a podcast and then switches to a YouTube film keeps
  // it, because both offer that answer under that key. Where the lists differ
  // („Więcej niż 4" is `5-plus`, „Więcej niż 5" is `6-plus`) the answer is
  // dropped by itself — see `chooseSessionType`.
  youtube: () => [
    {
      key: 'people',
      label: 'Ile osób będzie jednocześnie przed kamerą?',
      choices: [
        { value: '1', label: '1 osoba', short: '1 os.' },
        { value: '2', label: '2 osoby', short: '2 os.' },
        { value: '3', label: '3 osoby', short: '3 os.' },
        { value: '4', label: '4 osoby', short: '4 os.' },
        { value: '5-plus', label: 'Więcej niż 4 osoby', short: '5+ os.' },
      ],
      columns: 2,
      missing: 'liczbę osób',
      noun: 'Liczba osób',
    },
    {
      key: 'ujecia',
      label: 'Ilu ujęć potrzebujesz?',
      choices: [
        { value: '1', label: 'Jedno główne ujęcie', short: '1 ujęcie' },
        { value: '2', label: 'Dwa różne ujęcia', short: '2 ujęcia' },
        { value: '3', label: 'Trzy różne ujęcia', short: '3 ujęcia' },
      ],
      columns: 2,
      missing: 'liczbę ujęć',
      noun: 'Ujęcia',
    },
  ],
  podcast: () => [
    {
      key: 'people',
      // Owner: *„opcję jednej osoby usunąłbym, bo taki materiał lepiej pasuje
      // do filmu na YouTube albo kursu"* — so the list starts at two.
      label: 'Ile osób weźmie udział w rozmowie?',
      choices: [
        { value: '2', label: '2 osoby', short: '2 os.' },
        { value: '3', label: '3 osoby', short: '3 os.' },
        { value: '4', label: '4 osoby', short: '4 os.' },
        { value: '5', label: '5 osób', short: '5 os.' },
        { value: '6-plus', label: 'Więcej niż 5 osób', short: '6+ os.' },
      ],
      columns: 2,
      missing: 'liczbę osób',
      noun: 'Liczba osób',
    },
    {
      key: 'ujecia',
      label: 'Ilu ujęć potrzebujesz?',
      choices: [
        { value: 'wspolne', label: 'Jedno ujęcie całej rozmowy', short: '1 ujęcie' },
        { value: 'osobne', label: 'Osobne ujęcia rozmówców', short: 'osobne ujęcia' },
        {
          value: 'osobne-plus',
          label: 'Osobne ujęcia i wspólny plan',
          short: 'osobne i wspólny',
        },
      ],
      columns: 2,
      missing: 'liczbę ujęć',
      noun: 'Ujęcia',
    },
  ],
  rolki: () => [
    {
      key: 'materialy',
      // The owner's note above this table: „przy rolkach pytanie o format
      // pomijamy, automatycznie wybieramy nagranie pionowe" — which is why
      // there is no orientation question here.
      label: 'Ile materiałów chcesz nagrać?',
      choices: [
        { value: '1-3', label: 'Od 1 do 3', short: '1–3 mat.' },
        { value: '4-6', label: 'Od 4 do 6', short: '4–6 mat.' },
        { value: '7-10', label: 'Od 7 do 10', short: '7–10 mat.' },
        { value: '11-20', label: 'Od 11 do 20', short: '11–20 mat.' },
        { value: '20-plus', label: 'Więcej niż 20', short: '20+ mat.' },
      ],
      columns: 3,
      missing: 'liczbę materiałów',
      noun: 'Liczba materiałów',
    },
    {
      key: 'people',
      label: 'Ile osób będzie występować?',
      choices: [
        { value: '1', label: '1 osoba', short: '1 os.' },
        { value: '2', label: '2 osoby', short: '2 os.' },
        { value: '3-5', label: 'Od 3 do 5 osób', short: '3–5 os.' },
        { value: '6-plus', label: 'Więcej niż 5 osób', short: '6+ os.' },
      ],
      columns: 2,
      missing: 'liczbę osób',
      noun: 'Liczba osób',
    },
  ],
  firmowy: () => [
    {
      key: 'lokalizacje',
      label: 'W ilu lokalizacjach nagrywamy?',
      choices: [
        { value: '1', label: '1 lokalizacja', short: '1 lok.' },
        { value: '2', label: '2 lokalizacje', short: '2 lok.' },
        { value: '3', label: '3 lokalizacje', short: '3 lok.' },
        { value: '4-plus', label: 'Więcej niż 3 lokalizacje', short: '4+ lok.' },
      ],
      // Owner: *„więcej niż 3 lokalizacje powinny prowadzić do indywidualnej
      // wyceny"*. The path ends here for that answer — no plan, no figure.
      quoteValues: ['4-plus'],
      columns: 2,
      missing: 'liczbę lokalizacji',
      noun: 'Lokalizacje',
    },
    {
      key: 'people',
      label: 'Ile osób wystąpi w materiale?',
      choices: [
        { value: '1', label: '1 osoba', short: '1 os.' },
        { value: '2', label: '2 osoby', short: '2 os.' },
        { value: '3-5', label: 'Od 3 do 5 osób', short: '3–5 os.' },
        { value: '6-10', label: 'Od 6 do 10 osób', short: '6–10 os.' },
        { value: '11-plus', label: 'Więcej niż 10 osób', short: '11+ os.' },
      ],
      columns: 2,
      missing: 'liczbę osób',
      noun: 'Liczba osób',
    },
    {
      key: 'miejsce',
      label: 'Czy nagrywamy wewnątrz, na zewnątrz czy w obu miejscach?',
      choices: [
        { value: 'wewnatrz', label: 'Wewnątrz', short: 'wewnątrz' },
        { value: 'zewnatrz', label: 'Na zewnątrz', short: 'na zewnątrz' },
        {
          value: 'wewnatrz-zewnatrz',
          label: 'Wewnątrz i na zewnątrz',
          short: 'wewnątrz i na zewnątrz',
        },
      ],
      columns: 2,
      missing: 'miejsce nagrania',
      noun: 'Miejsce',
    },
  ],
  kurs: () => [
    {
      key: 'lekcje',
      label: 'Ile lekcji chcesz nagrać?',
      choices: [
        { value: '1-3', label: 'Od 1 do 3', short: '1–3 lekcje' },
        { value: '4-6', label: 'Od 4 do 6', short: '4–6 lekcji' },
        { value: '7-10', label: 'Od 7 do 10', short: '7–10 lekcji' },
        { value: '11-20', label: 'Od 11 do 20', short: '11–20 lekcji' },
        { value: '20-plus', label: 'Więcej niż 20', short: '20+ lekcji' },
      ],
      columns: 3,
      missing: 'liczbę lekcji',
      noun: 'Liczba lekcji',
    },
    {
      key: 'people',
      label: 'Ile osób prowadzi kurs?',
      choices: [
        { value: '1', label: '1 osoba', short: '1 os.' },
        { value: '2', label: '2 osoby', short: '2 os.' },
        { value: '3', label: '3 osoby', short: '3 os.' },
        { value: '4-plus', label: 'Więcej niż 3 osoby', short: '4+ os.' },
      ],
      columns: 2,
      missing: 'liczbę prowadzących',
      noun: 'Prowadzący',
    },
    {
      key: 'ekran',
      label: 'Czy nagrywamy również prezentację lub ekran?',
      choices: [
        { value: 'tak', label: 'Tak', short: 'z ekranem' },
        { value: 'nie', label: 'Nie', short: 'bez ekranu' },
      ],
      columns: 2,
      missing: 'odpowiedź o ekran',
      noun: 'Ekran',
    },
  ],
  event: () => [
    {
      key: 'zakres',
      label: 'Co ma obejmować nagranie?',
      choices: [
        { value: 'scena', label: 'Scenę i wystąpienia', short: 'scena' },
        {
          value: 'uczestnicy',
          label: 'Uczestników i atmosferę wydarzenia',
          short: 'uczestnicy',
        },
        { value: 'calosc', label: 'Całe wydarzenie', short: 'całe wydarzenie' },
      ],
      columns: 2,
      missing: 'zakres nagrania',
      noun: 'Zakres',
    },
    {
      key: 'przestrzenie',
      label: 'Czy wydarzenie odbywa się w jednej przestrzeni?',
      choices: [
        { value: 'jedna', label: 'Jedna przestrzeń', short: '1 przestrzeń' },
        {
          value: 'kilka',
          label: 'Kilka przestrzeni jednocześnie',
          short: 'kilka przestrzeni',
        },
      ],
      columns: 2,
      missing: 'liczbę przestrzeni',
      noun: 'Przestrzenie',
    },
    {
      key: 'dzwiek',
      label: 'Czy możemy pobrać dźwięk z nagłośnienia organizatora?',
      choices: [
        { value: 'tak', label: 'Tak', short: 'dźwięk z nagłośnienia' },
        { value: 'nie', label: 'Nie', short: 'własny dźwięk' },
        { value: 'nie-wiem', label: 'Jeszcze nie wiem', short: 'dźwięk do ustalenia' },
      ],
      columns: 3,
      missing: 'odpowiedź o dźwięk',
      noun: 'Dźwięk',
    },
  ],
  // The only session whose table answers just ONE of its questions: what kind
  // of place it is and which walls we photograph are still the flat's own.
  nieruchomosci: (table) => [
    {
      key: 'obiekt',
      label: 'Jaki obiekt fotografujemy?',
      choices: [
        { value: 'mieszkanie', label: 'Mieszkanie' },
        { value: 'dom', label: 'Dom' },
        { value: 'lokal', label: 'Lokal usługowy', short: 'Lokal' },
        { value: 'biuro', label: 'Biuro' },
        { value: 'inny', label: 'Inny obiekt', short: 'Inny' },
      ],
      columns: 3,
      missing: 'rodzaj obiektu',
      noun: 'Obiekt',
    },
    ...(table ? [table] : []),
    {
      key: 'zdjecia',
      label: 'Jakich zdjęć potrzebujesz?',
      choices: [
        { value: 'wnetrza', label: 'Wnętrza', short: 'wnętrza' },
        { value: 'zewnatrz', label: 'Zdjęcia z zewnątrz', short: 'z zewnątrz' },
        {
          value: 'wnetrza-zewnatrz',
          label: 'Wnętrza i zdjęcia z zewnątrz',
          short: 'wnętrza i zewnątrz',
        },
      ],
      columns: 2,
      missing: 'rodzaj zdjęć',
      noun: 'Zdjęcia',
    },
  ],
}

/** What this role, doing this kind of session, is asked. */
function detailQuestionsFor(
  tables: Record<string, SessionTable>,
  isShootDay: boolean,
  sessionType: string | null,
): readonly DetailQuestion[] {
  if (!isShootDay) return [MEETING_PEOPLE]
  const build = sessionType ? SESSION_QUESTIONS[sessionType] : undefined
  if (!build) return [ON_SET_PEOPLE]
  const table = sessionType ? tables[sessionType] : undefined
  const asked = build(table ? tableQuestionFor(table) : null)
  // A tabled session whose table did not arrive — the pricing read failed, or
  // the seed never ran — would otherwise show an empty panel with a „Dalej"
  // that goes nowhere. The old range question is the honest fallback: it still
  // tells the photographer what the day is, and the price falls back with it.
  return asked.length > 0 ? asked : [ON_SET_PEOPLE]
}

/**
 * WHAT EACH ROLE IS ASKED AFTER THE CITY, in that role's own words.
 *
 * Both shoot roles are asked „co to właściwie jest", and neither can be asked
 * it in the other's words: a photographer takes a SESJA and an operator records
 * a MATERIAŁ, so the question, the answers and the guide's title all change
 * with the role. This used to be one slug (`SESSION_TYPE_ROLE = 'fotograf'`)
 * and a single hard-wired list, which meant the operator skipped the question
 * entirely and went straight to counting heads.
 *
 * Keyed by SLUG rather than by name or icon, because the slug is the stable
 * identifier the rest of this file already links and prefills with. A role that
 * is not in here is not asked — which is right for the consultation, where the
 * only thing to establish is a time.
 */
const ROLE_KINDS: Record<
  string,
  {
    /** The caption over the answers, and the aria-label of the panel. */
    label: string
    /** The button that opens the guide. */
    action: string
    /** The guide's own heading. */
    guide: string
    /** „Gdzie fotografujemy?" / „Gdzie nagrywamy?" — step two's title. */
    where: string
    types: readonly (Choice & { blurb: string; Icon: LucideIcon })[]
  }
> = {
  fotograf: {
    label: 'Rodzaj sesji',
    action: 'Co to za sesje?',
    guide: 'Rodzaje sesji fotograficznych',
    where: 'Gdzie fotografujemy?',
    types: SESSION_TYPES,
  },
}

/**
 * This role's question — the photographer's six sessions, and nobody else's.
 *
 * The operator had six of his own here until 2026-10-12, built from
 * `recording_type`: „Film na YouTube", „Podcast lub wywiad" and the rest.
 * Owner: *„pytanie co [nagrywamy] znika w ogóle, to nie ma wpływu na wycenę;
 * zamiast tego jest pół planu czy cały plan"*. He was right that it priced
 * nothing — every `recording_type.client_net_minor` was NULL and always had
 * been — so the question collected an answer that changed no figure and asked
 * the visitor to make a decision the shop did not act on.
 *
 * `recording_type` itself stays: the panel still edits it, the cart column
 * still exists and is nullable, and an order placed before today keeps the
 * kind frozen in its metadata. What is gone is the ASKING.
 */
function roleKindsFor(slug: string) {
  return ROLE_KINDS[slug]
}

/**
 * The role whose price comes back as a discount code.
 *
 * `spec-yt` is `CONSULTATION_SLUG` in `@repo/core` — the key the mint rule and
 * every placed order resolve through — but core is not importable from a client
 * bundle (it pulls the database in with it), so the slug is written out here.
 * The two must not drift; if a second role ever earns its price back, this
 * stops being a slug and becomes a catalogue column.
 */
const REFUNDED_ROLE = 'spec-yt'

/**
 * When a shoot day can START.
 *
 * The owner's list, written out rather than derived from the day's length.
 * There used to be two — a three-hour half could start as late as 15:00 — and
 * the split they belonged to is gone. If the day ever gets real opening hours
 * this becomes `offering.open_from/open_to` minus the day's duration, and the
 * constant goes.
 *
 * `MEETING_STARTS` is not here on purpose: an hourly booking keeps the
 * calendar's own 09:00–16:00, which is what the studio and the contact form
 * use too.
 */

/**
 * The hours this role can start a shoot day at on `day` — from what performers
 * actually declared, falling back to the day's own list.
 *
 * ── WHAT THIS REPLACED ───────────────────────────────────────────────────
 * Two constants written out in this file, with a note admitting they were the
 * owner's lists rather than a rule and that they would go "if the day ever
 * gets real opening hours". It has: somebody now declares 09:00–12:00 and
 * 15:00–18:00 in the panel, and `offerableStartsFor` turns every qualified
 * person's windows into the hours an eight-hour day actually fits in.
 *
 * So the picker stops offering 11:00 to a team whose only photographer
 * finishes at two — which used to be a sale that failed at the till, with a
 * message about the wrong thing ("wybierz inny dzień", when the day was fine
 * and the hour was not).
 *
 * ── THE THREE STATES, AGAIN ──────────────────────────────────────────────
 * `openStartsByPlan === null` — the read failed, so fall back to `dayStarts()`
 * and let the claim be the judge. An empty picker and a booked-out team look
 * identical, and a timed-out query must never say the second.
 * A day PRESENT with no hours — nobody can start then. A real answer.
 * No day chosen yet — nothing to look up, so offer the standard list as a
 * preview of what step 3 will ask; narrowing to nothing would make the step
 * look broken to anybody who scrolled past step 2.
 */
function startsFor(
  role: SpecialistView,
  levelSlug: string | null,
  day: string | null,
): readonly string[] {
  const declared = role.openStartsByPlan
  if (!declared || !day || !levelSlug) return dayStarts()
  return declared[levelSlug]?.[day] ?? dayStarts()
}

type Props = {
  locale: string
  specialists: SpecialistView[]
  cities: CityView[]
  /**
   * The price model, read on the same request as the catalogue.
   *
   * `null` when that read failed. The page then asks its old range questions
   * and quotes the plan's own catalogue figure — the state it was in before any
   * of this existed, which is the right thing to degrade to: a shoot page that
   * cannot price is still a shoot page, and one that prices off half a model is
   * not.
   */
  pricing: ShootPricing | null
  /**
   * What the operator's day can have added to it, in NET grosze.
   *
   * Two numbers and not the rule set they come from: this is a client
   * component, so whatever it imports ships to the browser, and it needs two
   * figures rather than nineteen rules and the module around them.
   */
  extras: { readonly micPairNetMinor: number; readonly lightsNetMinor: number }
  /**
   * The specialist this page is ABOUT, when it is about one of them.
   *
   * `/pl/operator` and `/pl/fotograf` each pass their own slug; the page then
   * opens with that person already chosen, so the reader starts at „gdzie"
   * rather than answering a question they answered by clicking the tab. Left
   * out, the view behaves as it always did and the first step is the choice.
   */
  only?: string
}

export function ProductionPageView({ locale, specialists, cities, pricing, extras, only }: Props) {
  // `only` seeds the state rather than replacing it: everything downstream —
  // the price, the questions, the calendar — already reads `specialist`, and
  // a second source for "who is coming" is how the two would drift apart.
  //
  // It is translated from a SLUG to an id on the way in. The routes know the
  // person by slug, which is what their address says; `picked` below matches
  // on `id`, which is what the catalogue row carries. Seeding the slug
  // straight in left the card unpressed and step two locked behind a choice
  // that had already been made by opening the page.
  const [specialist, setSpecialist] = useState<string | null>(() =>
    only ? (specialists.find((one) => one.slug === only)?.id ?? null) : null,
  )
  const [city, setCity] = useState<string | null>(null)
  /**
   * Every answer the details column has been given, by question key.
   *
   * A map rather than a field each, because the questions themselves are not
   * fixed: a property shoot asks three, a portrait asks one, and the next
   * session type the owner adds will ask whatever its list says. A `useState`
   * per question would have to be written here before anybody could add one
   * there.
   */
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [sessionType, setSessionType] = useState<string | null>(null)
  /**
   * Which length of day — the operator's „Pół planu" or „Cały plan".
   *
   * Its own state rather than another entry in `answers`, because it is not a
   * question about the shoot: it is WHICH THING is being bought, the same kind
   * of fact as the city. It resolves to a catalogue row and it is what the
   * price is read off.
   */
  const [planSlug, setPlanSlug] = useState<string | null>(null)
  /**
   * Whether the middle column is explaining the session types instead of
   * asking its questions.
   *
   * A MODE, not an overlay. The panel it would float over is a scroll container
   * — `overflow-y-auto` clips anything absolutely positioned inside it — and
   * escaping that means measuring the trigger and positioning `fixed`, which is
   * a lot of machinery for a card. Swapping the panel's contents needs none of
   * it, has nowhere to be clipped, and on a phone is the only shape that works
   * anyway.
   */
  const [explaining, setExplaining] = useState(false)
  /**
   * Whether the six session pills are open although one is already chosen.
   *
   * The calendar's `browsing` again, for the calendar's reason: once the
   * question is answered the six answers are 91px of the column spent on
   * something already decided — and a property shoot asks three more questions
   * after this one, which do not fit behind it. Folded, it keeps the answer on
   * screen and hands the room to what is still being asked.
   *
   * Separate from `sessionType` so that re-opening the list does not throw the
   * answer away: somebody looking again at what „Kobieca" meant has not
   * withdrawn their „Nieruchomości".
   */
  /**
   * Which question the panel is showing.
   *
   * Owner: *„nie «Zmień», tylko «Wróć» — przez te panele idzie się po kolei,
   * każde pytanie = osobny panel i to się przesuwa, jak się udzieli
   * odpowiedzi"*. So this column is a path, not a form: one question fills it,
   * answering moves to the next, „Wróć" steps back. What was answered is not
   * repeated here — the summary in step three carries it, which is the column
   * whose job is to say what is being ordered.
   *
   * An INDEX into `steps`, which is derived every render and changes length
   * with the answers (a property shoot asks three questions where a portrait
   * asks one). It is clamped at render rather than corrected on write, so a
   * list that shrinks under it can never leave the panel blank.
   */
  const [step, setStep] = useState(0)
  /**
   * Which way the last move went, so the incoming panel slides from the side
   * it came from: forward enters from the right, „Wróć" from the left.
   *
   * Owner: *„niech będzie takie płynne przejście między tymi pytaniami"*. A
   * panel that simply swaps its contents reads as a redraw; one that arrives
   * from the direction of travel reads as a path — which is what this column
   * is. Stored rather than derived because the animation runs on the NEW panel
   * and by then the old index is gone.
   */
  const [goingBack, setGoingBack] = useState(false)
  const [slot, setSlot] = useState<BookingSlot>(null)

  const picked = specialists.find((s) => s.id === specialist)
  const isOnline = picked?.coverage === 'online'
  const availableCities = citiesFor(picked, cities, specialists)
  /**
   * Everything else in the catalogue: a city with nobody covering it.
   *
   * Derived, not flagged. Owner asked for Gdańsk, Kraków, Poznań and Rzeszów on
   * the map „jako wkrótce" — and „wkrótce" is exactly the state of a seeded
   * city no specialist lists. Giving somebody that city in the panel moves the
   * pin from promise to control with nothing here to remember.
   *
   * Hidden for a remote role, along with the whole map: there is nowhere to
   * come to, so a list of cities we will one day drive to answers a question
   * nobody asked.
   */
  const soonCities = isOnline
    ? []
    : cities.filter((city) => !availableCities.some((served) => served.id === city.id))
  const pickedCity = cities.find((c) => c.id === city)

  /**
   * The one level a shoot day is sold as.
   *
   * There used to be a PICKER here — „Cały plan (8 h)" against „Półplan (3 h)" —
   * and a whole step of the path devoted to it. The owner retired the split, so
   * the day is not chosen, it is simply what a shoot role is booked for. The
   * consultation has no such level and gets none of this, which follows from the
   * catalogue rather than from its slug.
   */
  const dayLevel = picked?.plans.find((one) => isShootDayLevel(one.slug)) ?? null

  /**
   * The levels a shoot day is actually SOLD at — priced ones only.
   *
   * The operator got two on 2026-10-12, „Pół planu" (5 h) and „Cały plan"
   * (8 h). His old single level is still in the catalogue and still active,
   * deliberately: performers hold `user_skill` grants against it and turning
   * it off would strip them. It carries no price, so filtering on that is what
   * keeps it out of the picker without touching anybody's grants.
   *
   * The photographer has one level and therefore never sees this step — the
   * list has to hold TWO before there is a choice to make.
   */
  const planLevels = (picked?.plans ?? []).filter(
    (one) => isShootDayLevel(one.slug) && !one.quoteOnly,
  )
  const asksPlan = planLevels.length > 1
  const pickedPlan = planLevels.find((one) => one.slug === planSlug) ?? null

  /**
   * The owner's tables, folded into the shape this column asks in.
   *
   * `useMemo` on the props and nothing else: the fold is pure, the props change
   * once per navigation, and rebuilding 87 rows on every keystroke of the path
   * would be work done for nobody.
   */
  const tables = useMemo(() => buildTables(pricing), [pricing])

  // Every role is asked something, and `isShootDay` words it rather than gates
  // it: a shoot has people in FRONT OF the lens, a planning meeting has people
  // AT it, and one label covering both would have to be vague enough to mean
  // neither.
  const questions = !picked
    ? []
    : picked.slug === OPERATOR_ROLE
      ? OPERATOR_QUESTIONS
      : detailQuestionsFor(tables, Boolean(picked.isShootDay), sessionType)
  /** A session read off a table, and what it asks once an answer lands. */
  const sessionTable = sessionType ? tables[sessionType] : undefined
  const quoted = isQuoted(tables, sessionType, answers, questions)
  /** The last question of a tabled session — computed, see `photoQuestionFor`. */
  const photoQuestion = quoted ? null : photoQuestionFor(tables, sessionType, answers, pricing)
  /**
   * What the whole path comes to. `null` until every part of it is answered.
   *
   * Two models, one per role, and never both on one path: a photographer's
   * session is priced by `shoot_rate`, an operator's day by the KIND of
   * recording. The operator's is `null` throughout today, because the owner has
   * not set those rates yet — see `recordingPriceOf`.
   */
  const priced = priceOf(pricing, tables, sessionType, answers)
  /**
   * …and what the OPERATOR's day comes to: the level he picked, plus what he
   * asked to have added to it.
   *
   * Stated prices, not a model: `priceShoot`'s margin band does not produce
   * 850 or 1200 (see the seed). So this is arithmetic on the catalogue figure
   * and two rules — no third source, and nothing the page invents.
   */
  const extraMicPairs = MIC_PAIRS[answers[MIC_KEY] ?? ''] ?? 0
  const wantsLights = answers[LIGHTS_KEY] === 'tak'
  const recordingPrice =
    pickedPlan && !pickedPlan.quoteOnly
      ? Math.round(pickedPlan.priceNet * 100) +
        extraMicPairs * extras.micPairNetMinor +
        (wantsLights ? extras.lightsNetMinor : 0)
      : null
  const kinds = picked ? roleKindsFor(picked.slug) : undefined
  /** Is this the role whose day is sold in two lengths? */
  const isRecordingRole = picked?.slug === OPERATOR_ROLE
  const asksSessionType = Boolean(kinds)
  const pickedSession = kinds?.types.find((type) => type.value === sessionType)
  /**
   * The photographer is asked what kind of session first, and nothing else
   * until it is answered: the questions below ARE the session's own — a head
   * count answered before „Nieruchomości" was chosen is an answer to a question
   * that stops existing the moment it is.
   */
  /**
   * A remote role is never asked where — there is nowhere to come to — and
   * neither is a role nobody covers a city for, which would be a question with
   * no answers to it.
   */
  const asksCity = Boolean(picked) && !isOnline && availableCities.length > 0

  /**
   * Every question this role asks, in the order it asks them: one per panel.
   *
   * Derived, never stored. The middle of the list belongs to the session type —
   * „Nieruchomości" asks three things where „Portretowa" asks one — so the path
   * is rebuilt whenever an answer changes it, and `step` is simply an index
   * into whatever it currently is.
   *
   * The session's own questions wait for the session: asked before it, they
   * would be answering for a session that stops existing the moment one is
   * chosen. The plan is always last, so the price arrives as the answer to what
   * was described rather than as a rate somebody has to talk down.
   */
  const steps = !picked
    ? []
    : [
        ...(asksCity ? [{ kind: 'city' as const, key: 'city' }] : []),
        // Where „Co nagrywamy?" stood until 2026-10-12. The owner swapped it
        // for the thing that actually moves the bill: *„zamiast tego jest pół
        // planu czy cały plan"*.
        ...(asksPlan ? [{ kind: 'plan' as const, key: 'plan' }] : []),
        ...(asksSessionType ? [{ kind: 'session' as const, key: 'session' }] : []),
        ...((asksSessionType && !sessionType) || (asksPlan && !planSlug)
          ? []
          : questions.map((question) => ({
              kind: 'question' as const,
              key: question.key,
              question,
            }))),
        // A quoted row skips the photo count: the table offers none at „Więcej
        // niż 300 m²", and a panel whose every answer is greyed out is a wall,
        // not a step. The price at the end says what happens instead.
        //
        // The PLAN used to be a step of its own right here, between the
        // questions and the photo count. It is gone with the split it asked
        // about.
        ...(photoQuestion
          ? [{ kind: 'question' as const, key: photoQuestion.key, question: photoQuestion }]
          : []),
        // …and the figure last of all. Owner: „ile zdjęć chcesz z tego uzyskać,
        // po tym pojawia się cena". The path ends on what it costs, which is
        // also the only panel that asks nothing.
        ...(dayLevel ? [{ kind: 'price' as const, key: 'price' }] : []),
      ]
  /**
   * Everything answered so far, in the order the PATH asks it, for the summary.
   *
   * Read off `steps` rather than off the role's questions, because those two
   * disagree: with no session type chosen the photographer's own questions are
   * not on the path at all, and a head count left over from a session that was
   * changed would go on being printed under a path that never asks for it.
   *
   * `short` where a choice has one, because that box shares a single line with
   * a role, a plan, a city and a date: „Wnętrza i zdjęcia z zewnątrz" spelt out
   * pushes the date off the end of a summary meant to be read at a glance.
   */
  const answered = steps
    .map((entry) => {
      if (entry.kind !== 'question') return null
      const choice = entry.question.choices.find((c) => c.value === answers[entry.question.key])
      return choice ? (choice.short ?? choice.label) : null
    })
    .filter((word): word is string => word !== null)
    .join(' · ')
  /**
   * Is step two finished — every panel on the path answered?
   *
   * Owner's flow: *„najpierw klient musi wybrać kogo potrzebuje, na punkty 2 i
   * 3 jest blur […] gdy przeklika wszystko to odbluruje się punkt 3"*. This is
   * the second half of it; the first is simply `picked`.
   *
   * Read off ANSWERS and not off `stepIndex`, for two reasons. The index says
   * where the reader is standing, not how much is filled in — so pressing
   * „Wróć" to re-read an answer would put the lock back on a column that was
   * already earned, which is a page taking something away for looking at it.
   * And `steps` grows as it is answered (a session type adds its own
   * questions), so "reached the last panel" is true of a list that is about to
   * get longer.
   *
   * The `price` panel asks nothing and is therefore always satisfied, which is
   * right: it is the path's receipt, not one of its questions.
   */
  const detailsDone =
    Boolean(picked) &&
    steps.length > 0 &&
    steps.every((entry) =>
      entry.kind === 'city'
        ? Boolean(pickedCity)
        : entry.kind === 'session'
          ? Boolean(sessionType)
          : entry.kind === 'question'
            ? Boolean(answers[entry.question.key])
            : true,
    )
  const stepIndex = Math.min(step, Math.max(0, steps.length - 1))
  const current = steps[stepIndex]
  /**
   * How many panels there are — once that is a number anybody can trust.
   *
   * Before a session type is chosen the photographer's own questions are not in
   * the list yet, so the path would promise „z 3" and become „z 6" the moment
   * „Nieruchomości" is pressed. A denominator that changes under the reader is
   * worse than no denominator, so until the session is known the counter says
   * „Krok 2" and stops there.
   */
  const stepTotal =
    (asksSessionType && !sessionType) || (asksPlan && !planSlug) ? null : steps.length
  const nextStep = steps[stepIndex + 1]
  /** Whether this panel has been answered — what „Dalej" needs to exist. */
  const stepAnswered =
    current?.kind === 'city'
      ? Boolean(pickedCity)
      : current?.kind === 'plan'
        ? Boolean(pickedPlan)
        : current?.kind === 'session'
          ? Boolean(sessionType)
          : current?.kind === 'question'
            ? Boolean(answers[current.question.key])
            : // The price asks nothing, so it is answered the moment it is shown —
              // otherwise „Wróć" would be the only control on the last panel.
              current?.kind === 'price'
  /**
   * Move into the panel whenever the path moves.
   *
   * The question is keyed, so every step replaces the whole block — which means
   * the button somebody just pressed no longer exists and focus falls back to
   * the document. A keyboard user would be dropped at the top of the page on
   * every answer, and a screen reader would say nothing at all about the
   * question that just arrived. Focusing the group announces its label („Krok 3
   * z 6: Jaki obiekt fotografujemy?") and puts Tab one press away from the
   * answers.
   *
   * `preventScroll`, because this page's whole promise is that it does not
   * move: the panel is a scroll container and focusing inside it would
   * otherwise scroll the answer into view.
   *
   * `explaining` is a dependency as well as the step, because the guide is a
   * MODE: opening it unmounts the whole three-column grid, step block included,
   * and closing it builds a new one. The step has not changed across that trip,
   * so an effect watching only the step would not fire — and the reader who
   * opened the guide from the keyboard would be dropped on `document.body`
   * twice over, once on the way in and once on the way back.
   */
  const stepRef = useRef<HTMLDivElement>(null)
  const guideRef = useRef<HTMLDivElement>(null)
  const currentKey = current?.key
  useEffect(() => {
    if (explaining) {
      guideRef.current?.focus({ preventScroll: true })
      return
    }
    if (!currentKey) return
    stepRef.current?.focus({ preventScroll: true })
  }, [currentKey, explaining])

  const goBack = () => {
    setGoingBack(true)
    setStep(Math.max(0, stepIndex - 1))
  }
  /** An answer moves the panel on; taking one back leaves it where it is. */
  const advance = (answered: unknown) => {
    if (answered === null || answered === undefined) return
    setGoingBack(false)
    setStep(stepIndex + 1)
  }

  /**
   * The heading of step two, which is its own open question while it has one.
   *
   * The verb comes from the role, because „Gdzie fotografujemy?" over the
   * operator's cities would be the page using the wrong word for its own offer
   * — he shoots a nagranie. Same reason the session types belong to the
   * photographer alone.
   */
  const stepTwoTitle =
    picked && current?.kind === 'city' ? (kinds?.where ?? 'Gdzie pracujemy?') : 'Ustal szczegóły'
  // The figure comes from the catalogue rather than from a constant here, so
  // the promise and the price the card quotes can never disagree — a hardcoded
  // "199 zł" would go on saying 199 the day somebody edits the row in the panel.
  const refundPrice = picked?.slug === REFUNDED_ROLE ? picked.plans[0]?.price : undefined

  /**
   * The days step 3 lets somebody click, from what performers declared.
   *
   * `undefined` for anything that is not a shoot day, which hands the calendar
   * back its weekday rule — the meeting is booked out of a published calendar
   * rather than out of `capacity_day`, so a declared list for it would be
   * empty and would read as "booked out".
   *
   * ONE list, where there used to be two that excluded each other. The day is
   * not sold in shapes any more, so the days somebody declared for the shoot-day
   * level are simply the days on offer. A role whose level is missing falls back
   * to the union of whatever the map holds, which is the same answer it gave
   * before anybody had picked a shape.
   */
  const openDaysByPlan = picked?.isShootDay ? picked.openDaysByPlan : null
  const openDayChoices = !openDaysByPlan
    ? undefined
    : dayLevel
      ? (openDaysByPlan[dayLevel.slug] ?? [])
      : [...new Set(Object.values(openDaysByPlan).flat())].sort()

  /**
   * The hours step 3 offers, which depend on what is being booked.
   *
   * `undefined` hands the calendar back its own default — the right answer for
   * the meeting, and for anything hourly added later.
   *
   * A shoot day gets the hours somebody actually declared, and the owner's own
   * list where nobody has. It used to be two lists — a short day could begin as
   * late as 15:00 — and with the halves gone there is one.
   */
  const slotChoices = !picked?.isShootDay
    ? undefined
    : startsFor(picked, dayLevel?.slug ?? null, slot?.date ?? null)

  /** Drop the photo count — what both rules below do when its key changes. */
  const dropPhotoCount = (given: Record<string, string>) => {
    const { [PHOTO_COUNT]: _dropped, ...rest } = given
    return rest
  }

  /**
   * Pick the kind of session — and drop a count the new one cannot mean.
   *
   * The questions are not one list: a family counts 2 to 6, a product shoot
   * counts products, a property is asked three things and counted at none of
   * them. Carried over, `3-5` would leave the price gate satisfied, the pills
   * showing nothing chosen, and the summary printing an answer this session
   * never offered — so an answer survives only where the new session asks the
   * same question AND still offers that value.
   */
  const chooseSessionType = (next: string | null) => {
    setSessionType(next)
    const asked = detailQuestionsFor(tables, Boolean(picked?.isShootDay), next)
    setAnswers((given) => {
      const kept: Record<string, string> = {}
      for (const question of asked) {
        const answer = given[question.key]
        if (answer && question.choices.some((choice) => choice.value === answer)) {
          kept[question.key] = answer
        }
      }
      return kept
    })
  }

  /** Answer one question, or take the answer back by pressing it again. */
  const answerQuestion = (key: string, next: string | null) => {
    setAnswers((given) => {
      const { [key]: _dropped, ...rest } = given
      // The head count is the other half of the photo question's key, so
      // changing it invalidates whatever was chosen there — five people get
      // „25, 40 lub 50" and three get „20, 30 lub 40", and 25 is in one list
      // and not the other.
      const kept = key === 'people' ? dropPhotoCount(rest) : rest
      return next === null ? kept : { ...kept, [key]: next }
    })

    // The head count used to be able to strand a PLAN — six people cannot be
    // shot in three hours — and the plan then had to be dropped with it. With
    // one day level there is nothing left to strand: an answer the table does
    // not sell simply produces no photo counts, and the path says so.
    const table = sessionType ? tables[sessionType] : undefined
    if (table && key === table.key) setAnswers(dropPhotoCount)
  }

  /**
   * Specialists cover different cities, so a switch can strand a selection that
   * the new specialist doesn't serve. Clearing it here — rather than filtering
   * it out at render — keeps the enquiry link from carrying a city nobody
   * offers.
   */
  const chooseSpecialist = (id: string) => {
    const next = specialist === id ? null : id
    setSpecialist(next)

    // These two are not offered by every role,
    // so a kept answer could sit invisibly on a card that never asked it and
    // then travel into the enquiry.
    setAnswers({})
    setSessionType(null)
    setStep(0)
    // The hours differ between a shoot and a meeting — 08:00 is offered by one
    // and not the other — so a kept time can be one the new role never offers.
    setSlot(null)

    const stillOffered = citiesFor(
      specialists.find((s) => s.id === next),
      cities,
      specialists,
    ).some((c) => c.id === city)
    if (!stillOffered) setCity(null)
  }

  /*
   * NO ENQUIRY LINK. "Wolę zapytać" sat beside the creator button and carried
   * every answer as query parameters — `usluga`, `miasto`, `pakiet`, `osoby`,
   * `sesja`, `data`, `godz`.
   *
   * Two reasons it is gone, and the owner's is the first: the creator exists so
   * that all of this is priced automatically, and offering "ask us instead" at
   * the end of a form that has just collected everything invites the slow path
   * over the finished one.
   *
   * The second is that the link never worked as documented. `/pl/kontakt` is a
   * static page that reads no search params — nothing on the other end ever saw
   * a single one of those values, so the comment promising the team "would not
   * ask again" had been false since it was written.
   */
  /**
   * The same three answers, addressed to the creator instead of the inbox.
   *
   * A shoot is ordered like anything else here — it just carries a day and a
   * city, which is what `bookingMode = DAY` and `coverageMode = CITIES` say
   * about it. Handing off means this page never grew its own basket logic, and
   * the creator needed no branch for "production": it reads the same two
   * columns it reads for a camera and a room.
   */
  const orderHref = picked
    ? creatorHref(locale, {
        offeringSlug: picked.slug,
        // WHAT WAS ACTUALLY CONFIGURED, carried to the basket.
        //
        // Without these three the creator received a role and a plan, and
        // priced the day at the plan's catalogue rate: this page quoted
        // 2 450 zł and the basket charged 400. The link is the only place the
        // two meet, so it carries the whole answer — and `applyPrefill` checks
        // it against the offering's own rate card rather than trusting it.
        ...(priced && sessionTable && answers[sessionTable.key] && answers[PHOTO_COUNT]
          ? {
              shootSession: sessionType ?? undefined,
              shootScope: answers[sessionTable.key],
              shootPhotos: Number(answers[PHOTO_COUNT]),
            }
          : {}),
        // WHICH LEVEL is being bought. The chosen one wins over the first
        // one found: the operator has two („Pół planu", „Cały plan") plus the
        // unpriced one his performers still hold grants against, and
        // `dayLevel` finds whichever the catalogue lists first. A role with a
        // single level keeps the old behaviour, and the consultation — which
        // has no such level — carries none.
        //
        // The recording KIND used to travel here as well, in its own field.
        // The question that collected it is gone (owner, 2026-10-12: it priced
        // nothing), and the level is what moves the bill now.
        ...((pickedPlan ?? dayLevel) ? { variantSlug: (pickedPlan ?? dayLevel)?.slug } : {}),
        // The HOUR travels with the date. Step 3 has always asked for it and
        // this link used to drop it, so the booking went in all-day and the
        // availability check was handed a midnight-to-midnight span that no
        // declared window can cover — see `needsTime` in lib/creator.ts.
        ...(slot ? { from: slot.date, time: slot.time } : {}),
        ...(!isOnline && pickedCity ? { citySlug: pickedCity.slug } : {}),
      })
    : null

  return (
    <div className="relative w-full overflow-x-hidden">
      <div className={cn(CONTAINER, SCREEN_TOP, 'relative z-10')}>
        {/* ── THE WHOLE PATH, ON ONE SCREEN ──────────────────────────────
            Owner: *„to przebudowa też na zasadzie one page website"* — the
            same call already made for /pl/grafika and /pl/kontakt.

            It used to be three sections stacked down the page: cards, then a
            panel with the map, then a calendar, then the summary — 2200px of
            it, so the reader chose a specialist and then lost sight of that
            choice while scrolling to the next question. THREE COLUMNS instead,
            left to right, because that is what the page actually is: who, what,
            when. Every answer stays visible while the next one is given, and
            the summary sits under the calendar where the path ends rather than
            at the bottom of a scroll.

            Below `xl` it is one column and the page scrolls, which is the
            honest end of this: measured at 1024px, three tracks came out 278 /
            361 / 292 and the card's name wrapped into its own price. 1280 is
            where three columns are three usable ones. */}
        <div className={SCREEN_LOCK}>
          <PageHeader fit>
            {/* „Wybierz człowieka. Wybierz termin." — owner: *„zmieniamy, to
                dziwnie brzmi"*, and it did: the same verb twice, and „wybierz
                człowieka" reads like picking one off a shelf. The two beats
                stay, because that is how every headline on this site is built
                („Oddaj nam montaż. Wróć do nagrywania."), but they now say
                what the page is FOR rather than narrating its own form.

                Owner's own wording, and it landed once the strategy meeting
                moved out: a page holding an operator and a photographer sells
                one thing — a day with a crew on it — so the headline can name
                it instead of covering three unrelated roles at once.

                It NAMES BOTH ROLES, and that is the search engine's half of the
                bargain rather than a stylistic choice. „Zamów ekipę na swój
                dzień zdjęciowy" said the same thing without either word a
                person actually types, and an h1 carries far more weight than
                the address does — which is what the move to
                `/pl/fotograf-operator` was for. The title and the two cards
                already carried the words; the headline was the one place they
                were missing.

                „Operator KAMERY", not „operator": the full phrase is what
                somebody types, and the accent starts at „na", so the line
                breaks where the owner wrote it — the two roles on one line,
                what they are booked for on the next.

                That second line is „Twój kolejny projekt", not „Twój dzień
                zdjęciowy", by the owner's choice. It reads to somebody who has
                not yet decided their shoot IS a shoot day, and the phrase is
                not lost: the page title still carries it and both cards price
                in it. */}
            {/* ONE NAME when the page is about one person. „Fotograf i
                operator kamery" was right while both lived here and is a
                mismatch now: somebody who clicked „Fotograf" must not be
                greeted by a headline that leads with the operator. */}
            {only === 'operator'
              ? 'Operator kamery '
              : only === 'fotograf'
                ? 'Fotograf '
                : 'Fotograf i operator kamery '}
            <span className={ACCENT_TEXT}>na Twój kolejny projekt.</span>
          </PageHeader>

          {/* ── WHAT THE SIX SESSIONS ARE ──────────────────────────────
              The whole row, not a corner of it. Six descriptions in one column
              scrolled by 183px — a reading view that has to be scrolled is a
              reading view nobody finishes — and the page already owns 1400px it
              is not using while the questions are stood down.

              It is also a PICKER. A list that explains six things and then
              sends you back to press one of them somewhere else has wasted the
              trip, so choosing here selects the type and returns.

              A MODE rather than an overlay: the panel this would float over is
              a scroll container, which clips anything absolutely positioned
              inside it, and escaping that means measuring the trigger and
              positioning `fixed` — a lot of machinery for a card. */}
          {explaining ? (
            <div
              ref={guideRef}
              tabIndex={-1}
              role="group"
              aria-labelledby="flou-guide-title"
              className="mt-[clamp(0.75rem,2.4svh,1.75rem)] flex min-h-0 flex-1 flex-col rounded-3xl border border-white/[0.14] bg-white/[0.06] p-[clamp(0.85rem,1.9svh,1.5rem)] outline-none backdrop-blur-md"
            >
              <div className="flex items-center justify-between gap-3">
                <h2 id="flou-guide-title" className={cn(SUBHEADING, 'text-white')}>
                  {kinds?.guide ?? 'Co robimy'}
                </h2>
                <button
                  type="button"
                  onClick={() => setExplaining(false)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-[0.8rem] font-semibold text-white/70 transition hover:border-white/40 hover:text-white"
                >
                  <ArrowLeft size={14} aria-hidden="true" />
                  Wróć do wyboru
                </button>
              </div>

              <ul className="mt-[clamp(0.5rem,1.4svh,1rem)] grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-[clamp(0.4rem,1.1svh,0.75rem)] sm:grid-cols-2 xl:grid-cols-3">
                {(kinds?.types ?? []).map((type) => {
                  const isOn = sessionType === type.value
                  return (
                    <li key={type.value} className="min-h-0">
                      <button
                        type="button"
                        onClick={() => {
                          // The guide is a PICKER as well as an explanation —
                          // its own footer says „wybierz rodzaj, a wrócimy do
                          // reszty pytań" — so choosing here has to move the
                          // path exactly as choosing on the pills does. It used
                          // to set the session and drop the reader back on the
                          // question they had just answered.
                          chooseSessionType(type.value)
                          advance(type.value)
                          setExplaining(false)
                        }}
                        aria-pressed={isOn}
                        className={cn(
                          'h-full w-full rounded-2xl border px-4 py-[clamp(0.5rem,1.3svh,0.9rem)] text-left transition',
                          isOn
                            ? PICKED_FILL
                            : 'border-white/10 bg-white/[0.04] hover:border-white/30 hover:bg-white/[0.07]',
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <type.Icon
                            size={15}
                            aria-hidden="true"
                            className={cn('shrink-0', isOn ? '' : 'text-white/70')}
                          />
                          <span className="text-[length:clamp(0.9rem,2svh,1rem)] font-semibold">
                            {type.label}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'mt-1.5 block text-[length:clamp(0.78rem,1.7svh,0.875rem)] leading-snug',
                            isOn ? 'text-neutral-900/70' : 'text-white/55',
                          )}
                        >
                          {type.blurb}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>

              <p className={cn(MICRO, 'mt-[clamp(0.35rem,1svh,0.75rem)] text-white/45')}>
                Wybierz rodzaj, a wrócimy do reszty pytań.
              </p>
            </div>
          ) : (
            <div className="mt-[clamp(0.75rem,2.4svh,1.75rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.75rem,2svh,1.5rem)] xl:grid-cols-[1fr_1.3fr_1.05fr]">
              {/* ── STEP 1 · SPECIALIST ────────────────────────── */}
              <section aria-label="Wybór specjalisty" className="flex min-h-0 flex-col">
                <StepLabel n={1} title="Kogo potrzebujesz?" />

                {/* STACKED, not a three-up row. The row was right when this step
                  owned the full width of the page; in a third of it, three
                  tracks would be 150px each. Stacking also turns each card
                  from a poster into a LINE — name and price on one row, the
                  deliverables on one more — which is what a column this narrow
                  can hold without cutting anything.

                  `auto-rows-fr` so the three share the column evenly however
                  tall it turns out to be: on a 1440-tall monitor they grow
                  together instead of one long card and two short ones. */}
                <ul className="mt-[clamp(0.6rem,1.6svh,1.25rem)] grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-[clamp(0.5rem,1.4svh,0.875rem)]">
                  {specialists.map((item) => {
                    const Icon = specialistIcon(item.icon)
                    const tone = specialistTone(item.icon)
                    const isOn = specialist === item.id
                    return (
                      <li key={item.id} className="min-h-0">
                        <TiltCard className="h-full" strength={6}>
                          <button
                            type="button"
                            onClick={() => chooseSpecialist(item.id)}
                            aria-pressed={isOn}
                            className={cn(
                              'group relative flex h-full w-full flex-col justify-center gap-[clamp(0.4rem,1.1svh,0.75rem)] overflow-hidden rounded-3xl border p-[clamp(0.65rem,1.6svh,1.125rem)] text-left transition-colors duration-300',
                              // The picked card carries the brand ring and a glow
                              // — the same treatment the recommended tier gets on
                              // /pl/montaz and the chosen route on /pl/kontakt. It
                              // used to go `border-white/45`, which reads as
                              // "hovered" rather than "chosen" and is the one
                              // state a choice this page is built around cannot
                              // afford to be vague about.
                              isOn
                                ? PICKED_RING
                                : 'border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]',
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className="tilt-sheen pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300 [.tilt-card:hover_&]:opacity-100"
                            />

                            {/* ONE ROW: disc, name, price. The price used to sit
                              four lines below the name at the card's foot, so a
                              column of three cards asked the reader to travel
                              the full height of each one to compare the only
                              two things that differ between them. */}
                            <span className="relative flex items-center gap-3">
                              {/* The role's own colour, and ALWAYS — not only
                                once it is picked. Three grey discs told the
                                reader nothing until they had read three
                                headings; three colours are three choices at a
                                glance. */}
                              <span
                                className="flex size-[clamp(2rem,4svh,2.5rem)] shrink-0 items-center justify-center rounded-full transition-colors duration-300"
                                style={{
                                  color: isOn ? 'oklch(0.14 0.02 300)' : tone,
                                  backgroundColor: isOn
                                    ? tone
                                    : `color-mix(in oklab, ${tone} 18%, transparent)`,
                                  boxShadow: isOn ? `0 12px 30px -14px ${tone}` : undefined,
                                }}
                              >
                                <Icon size={16} strokeWidth={2.1} aria-hidden="true" />
                              </span>

                              {/* Name only. The uppercase role sat under it —
                                "Strateg kanału" beneath "Konsultacja YouTube" —
                                restating the card's own heading in a second,
                                smaller voice. */}
                              <span className="min-w-0 flex-1 text-[length:clamp(0.9rem,2svh,1rem)] leading-snug font-bold text-white">
                                {item.name}
                              </span>

                              <span
                                className={cn(
                                  STAT,
                                  'shrink-0 text-[length:clamp(1rem,2.4svh,1.25rem)] text-white',
                                )}
                              >
                                {item.priceLabel}
                                {item.priceUnit ? (
                                  <span className="ml-1 text-[0.7rem] font-medium text-white/55">
                                    / {item.priceUnit}
                                  </span>
                                ) : null}
                              </span>
                            </span>

                            {/* THE TICKS ARE THE CARD. Owner: *„zamiast
                              rozpisywania się dajemy zwyczajnie 4 ptaszki z
                              powiedzeniem, co zawiera dany pakiet"*.

                              A three-line paragraph used to sit above them,
                              cut to two lines by this column's width, saying in
                              prose what the ticks under it say as a list —
                              "Obróbka wliczona w stawkę" over „Zdjęcia po
                              obróbce". The paragraph is gone and the list took
                              its four claims (see `deliverables` in the seed),
                              so nothing was lost with it.

                              ONE COLUMN, four rows. Two columns meant two rows
                              of ticks, and two rows stretched across a 304px
                              card leaves a 100px hole between them — owner, of
                              exactly that: *„to dalej średnio rozłożone, sporo
                              pustej przestrzeni"*. Four rows have three gaps
                              instead of one, so the same four claims spread
                              evenly down the card instead of clumping at its
                              ends. The lines are short enough that a column
                              this wide never wraps one.

                              SPREAD THROUGH THE CARD, not huddled in the
                              middle of it. Owner: *„rozłóż treści w tych
                              kafelkach proporcjonalnie do ich wysokości"* —
                              with the city list gone from this column the two
                              cards grew to 304px each and the name and its four
                              ticks sat in the centre with 80px of nothing above
                              and below. The list takes the room under the name
                              (`flex-1`), its two rows share that room
                              (`auto-rows-fr`), and each tick sits in the middle
                              of its own row, so the card fills evenly at any
                              height the column lands on. */}
                            <span className="relative grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-y-[clamp(0.15rem,0.5svh,0.35rem)] border-t border-white/10 pt-[clamp(0.35rem,1svh,0.625rem)]">
                              {item.deliverables.map((d) => (
                                <span
                                  key={d}
                                  className="flex items-center gap-2 text-[length:clamp(0.75rem,1.6svh,0.875rem)] leading-snug text-white/60"
                                >
                                  <Check
                                    size={12}
                                    className="shrink-0"
                                    style={{ color: tone }}
                                    aria-hidden="true"
                                  />
                                  {d}
                                </span>
                              ))}
                            </span>
                          </button>
                        </TiltCard>
                      </li>
                    )
                  })}
                </ul>

                {/* „Stawki orientacyjne, wycenę potwierdzamy po rozmowie."
                    used to sit here and it is gone: owner, *„ceny są odgórnie
                    ustawione"*. It hedged a number the checkout charges to the
                    grosz — a footnote inviting a haggle over a price nobody is
                    going to move. */}
                {specialists.length === 0 ? (
                  <p className="mt-[clamp(0.5rem,1.4svh,1rem)] rounded-2xl border border-white/[0.12] bg-white/[0.04] px-5 py-4 text-[0.95rem] text-white/55">
                    Właśnie kompletujemy ekipę — napisz, kogo potrzebujesz, a odezwiemy się z
                    konkretem.
                  </p>
                ) : null}

                {/* ── WHERE, AND IT IS THE MAP THAT ASKS ─────────
                  Owner's layout: *„mapka jest interaktywna, na mapce klikamy
                  miasto"*. The city list is gone; the pins are the control.

                  It sits UNDER the two people rather than beside the questions,
                  because it answers the same kind of question they do — who is
                  coming, and where they are coming to — while the middle column
                  is about the shoot itself. It also finally has room: as an
                  illustration squeezed under four questions it was the first
                  thing to collapse on a short screen, and here it takes
                  whatever the two cards leave.

                  `flex-1` and not a height: two cards and a map share one
                  column, and the map is the part that can give. */}
              </section>

              {/* ── STEP 2 · WHERE, THEN EVERYTHING ELSE ───────── */}
              {/* The step's heading is its OWN first question while that
                question is open — owner: *„jak klient wybierze kogo potrzebuje,
                to wyświetla się 2. Gdzie nagrywamy / Gdzie fotografujemy […] i
                nagłówek 2 zmienia się na Ustal szczegóły"*. The city used to be
                a box in column one; a step that asks one thing at a time can
                ask it in its own title, and column one goes back to being the
                one question it is named after. */}
              <section aria-label="Szczegóły zlecenia" className="flex min-h-0 flex-col">
                <StepLabel n={2} title={stepTwoTitle} locked={!picked} />
                <StepGate locked={!picked} hint="Najpierw wybierz, kogo potrzebujesz">
                  {/* Lifted off the page rather than sunk into it. At
                  `bg-white/[0.04]` on brand black this panel measured barely
                  lighter than the section around it, so the step that holds
                  half the decisions read as background — a hairline drawn
                  around nothing.

                  `overflow-y-auto` is the ONE scroll this page keeps, and it is
                  deliberate: this column's questions appear and disappear with
                  the role (a shoot asks about session type and head count, a
                  meeting asks neither), so its height is the only one here that
                  cannot be known in advance. It scrolls inside its own panel
                  rather than pushing the calendar beside it off the screen. */}
                  <div className="mt-[clamp(0.6rem,1.6svh,1.25rem)] flex min-h-0 flex-1 flex-col gap-[clamp(0.4rem,1.15svh,0.875rem)] overflow-y-auto rounded-3xl border border-white/[0.14] bg-white/[0.06] p-[clamp(0.85rem,1.9svh,1.5rem)] shadow-[0_40px_120px_-80px_var(--flou-glow-core)] backdrop-blur-md">
                    {/* The offer's whole argument, and it belongs on the screen
                      where somebody is deciding — not in a confirmation email
                      after they have already paid. The code is minted when the
                      meeting is marked done (see `mintConsultationBonus` in
                      bookings.service.ts) and comes off whatever they order next,
                      at any size. */}
                    {refundPrice ? (
                      <p className="rounded-2xl border border-white/[0.14] bg-white/[0.05] px-4 py-3 text-[length:clamp(0.8rem,1.75svh,0.875rem)] leading-relaxed text-white/75">
                        <span className="font-semibold text-white">Spotkanie wraca do Ciebie.</span>{' '}
                        Po spotkaniu dostajesz od nas kod na {refundPrice} — jeśli zdecydujesz się
                        na współpracę, ta kwota schodzi z Twojego zlecenia.
                      </p>
                    ) : null}

                    {isOnline ? (
                      <p className="inline-flex items-center gap-2 self-start rounded-2xl border border-white/[0.14] bg-white/[0.05] px-4 py-3 text-[0.95rem] font-semibold text-white">
                        <Globe size={15} strokeWidth={2.2} aria-hidden="true" />
                        Zdalnie, cała Polska
                      </p>
                    ) : null}

                    {/* ── ONE QUESTION, ONE PANEL ─────────────────────
                      Owner: *„nie «Zmień», tylko «Wróć» — przez te panele
                      idzie się po kolei, każde pytanie = osobny panel i to się
                      przesuwa, jak się udzieli odpowiedzi"*.

                      It was a stack: answered questions folded to a line and
                      the open one sat under them, which is a form being filled
                      in — every answer still on screen, every one of them still
                      asking to be checked. A path shows the question it is on
                      and nothing else. What was answered is not lost; it is in
                      the summary in step three, which is the column whose job
                      is to say what is being ordered.

                      „Wróć" and not „Zmień" for the same reason: there is
                      nothing to edit here, only a step to go back to. */}
                    {current ? (
                      <>
                        <div className="flex shrink-0 items-center justify-between gap-3">
                          {stepIndex > 0 ? (
                            <button
                              type="button"
                              onClick={goBack}
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[0.7rem] font-semibold text-white/60 transition hover:border-white/40 hover:text-white"
                            >
                              <ArrowLeft size={12} aria-hidden="true" />
                              Wróć
                            </button>
                          ) : (
                            // Holds the left end of the row so the counter stays
                            // where it is on the first panel, instead of sliding
                            // across the moment „Wróć" appears.
                            <span aria-hidden="true" />
                          )}

                          <span className={cn(MICRO, 'shrink-0 text-white/40')}>
                            Krok {stepIndex + 1}
                            {stepTotal ? ` z ${stepTotal}` : null}
                          </span>

                          {/* THE WAY FORWARD, once this panel has its answer.
                            Answering moves the path on its own, so this is for
                            the panel somebody came BACK to: its answer is
                            already chosen, and without this the only control
                            that moves is the answer itself — which on a second
                            press used to un-choose it and go nowhere (now it
                            re-confirms, see `sticky`). „Wróć" without a „Dalej"
                            is also a one-way door for anybody on a keyboard. */}
                          {stepAnswered && nextStep ? (
                            <button
                              type="button"
                              onClick={() => {
                                setGoingBack(false)
                                setStep(stepIndex + 1)
                              }}
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[0.7rem] font-semibold text-white/60 transition hover:border-white/40 hover:text-white"
                            >
                              Dalej
                              <ArrowRight size={12} aria-hidden="true" />
                            </button>
                          ) : (
                            <span aria-hidden="true" />
                          )}
                        </div>

                        {/* The question takes the panel — width AND height, at
                          the owner's word: *„te pytania i miasta muszą być
                          dopasowane do szerokości i wysokości kafelka"*. The
                          answers stretch into it (capped, see `PillGroup`'s
                          `fill`), and whatever is left over is split above and
                          below so nothing hangs from the top.

                          `key` on the step is what makes the slide happen: a
                          new key is a new element, so the enter animation runs
                          again on every move, and `data-back` tells it which
                          side to come from. */}
                        <div
                          key={current.key}
                          ref={stepRef}
                          tabIndex={-1}
                          role="group"
                          aria-label={`Krok ${stepIndex + 1}${stepTotal ? ` z ${stepTotal}` : ''}: ${
                            current.kind === 'city'
                              ? stepTwoTitle
                              : current.kind === 'plan'
                                ? 'Jak długi dzień?'
                                : current.kind === 'session'
                                  ? (kinds?.label ?? 'Rodzaj')
                                  : current.kind === 'question'
                                    ? current.question.label
                                    : 'Cena'
                          }`}
                          data-back={goingBack ? '' : undefined}
                          className="flou-path-in flex min-h-0 flex-1 flex-col justify-center outline-none"
                        >
                          {current.kind === 'city' ? (
                            <div className="flex min-h-0 flex-1 flex-col justify-center gap-[clamp(0.3rem,0.8svh,0.625rem)]">
                              {/* A LIST, one city under another, each one the
                                full width of the panel — owner: *„ma być lista
                                wyboru, nie jedna obok drugiej, łącznie z tymi
                                «wkrótce», które są szare"*.

                                It was an interactive map of Poland in column
                                one, and the map was the owner's own idea. It
                                never got the room: 195px of height for an
                                outline as tall as it is wide rendered about
                                160px across in a 346px box — half the width
                                empty, the four „wkrótce" pins illegible. It
                                needs roughly 360px to be legible and there was
                                nowhere to take that from, so the pins became
                                what they always were underneath: a list of
                                places, and what we do at each.
                                `poland-map.tsx` is in the history. */}
                              <div
                                className="grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-[clamp(0.25rem,0.7svh,0.45rem)]"
                                style={{
                                  maxHeight: `${(availableCities.length + soonCities.length) * 4.5}rem`,
                                }}
                              >
                                {availableCities.map((item) => {
                                  const isOn = city === item.id
                                  return (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => {
                                        // Not a toggle, for the reason the
                                        // answers are not: on a path, pressing
                                        // the chosen city is „yes, this one" —
                                        // and clearing it would strand the panel
                                        // with nothing to move it on.
                                        setCity(item.id)
                                        advance(item.id)
                                      }}
                                      aria-pressed={isOn}
                                      className={cn(
                                        'flex h-full items-center gap-2.5 rounded-2xl border px-3 py-[clamp(0.35rem,1svh,0.7rem)] text-left transition',
                                        isOn
                                          ? PICKED_FILL
                                          : 'border-white/15 bg-white/[0.04] text-white/75 hover:border-white/35 hover:text-white',
                                      )}
                                    >
                                      <MapPin
                                        size={15}
                                        strokeWidth={2.2}
                                        aria-hidden="true"
                                        className="shrink-0"
                                      />
                                      {/* THE CITY, AND NOTHING ELSE. Owner:
                                        *„usuń te dopiski «zdjęcia i nagrania»,
                                        «baza, studio i sprzęt» — tylko miasto
                                        zostaje"*. The note said what we happen
                                        to keep in each place, which is our
                                        business and not an answer to „gdzie
                                        fotografujemy?". `city.note` still
                                        exists and is still read where it is
                                        the point — the assistant's catalogue —
                                        it is simply not this question. */}
                                      <span className="min-w-0 truncate text-[0.95rem] font-semibold">
                                        {item.name}
                                      </span>
                                    </button>
                                  )
                                })}

                                {/* The cities nobody covers yet, in the same list
                                  and greyed — the answer to „przyjedziecie do
                                  mnie?" for somebody in Kraków, given where
                                  they are looking for it. Not buttons and not
                                  disabled ones either: a disabled control
                                  invites a click that will never do anything,
                                  and this is a promise, not an option. */}
                                {soonCities.map((item) => (
                                  <div
                                    key={item.id}
                                    className="flex h-full items-center gap-2.5 rounded-2xl border border-dashed border-white/[0.09] px-3 py-[clamp(0.35rem,1svh,0.7rem)] text-white/30"
                                  >
                                    <MapPin
                                      size={15}
                                      strokeWidth={2.2}
                                      aria-hidden="true"
                                      className="shrink-0"
                                    />
                                    <span className="min-w-0 truncate text-[0.95rem] font-semibold">
                                      {item.name}
                                    </span>
                                    <span className="ml-auto shrink-0 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-white/30">
                                      wkrótce
                                    </span>
                                  </div>
                                ))}
                              </div>

                              <p className={cn(MICRO, 'shrink-0 leading-snug text-white/40')}>
                                Poza listą dojazd wyceniamy indywidualnie.
                              </p>
                            </div>
                          ) : current.kind === 'plan' ? (
                            /* Two levels, each carrying what it costs. The
                               price sits on the ANSWER rather than waiting for
                               the last panel, because here the figure IS the
                               trade-off: five hours against eight, 850 against
                               1200. Held back, it would make somebody choose a
                               length blind and meet the bill afterwards. */
                            <PillGroup
                              label="Jak długi dzień?"
                              choices={planLevels.map((level) => ({
                                value: level.slug,
                                // Name AND price in the one label. `PillGroup`
                                // has no second line to hang a figure on, and
                                // this is the one question whose figure cannot
                                // wait for the last panel: it IS the trade-off
                                // being made. Always a figure, never „wycena"
                                // — `planLevels` already dropped the unpriced.
                                label: `${level.name} · ${level.price}`,
                              }))}
                              value={planSlug}
                              onChange={(next) => {
                                setPlanSlug(next)
                                advance(next)
                              }}
                              columns={2}
                              fill
                              sticky
                            />
                          ) : current.kind === 'session' ? (
                            <PillGroup
                              label={kinds?.label ?? 'Rodzaj'}
                              choices={kinds?.types ?? []}
                              value={sessionType}
                              onChange={(next) => {
                                chooseSessionType(next)
                                advance(next)
                              }}
                              columns={3}
                              fill
                              sticky
                              action={
                                <button
                                  type="button"
                                  onClick={() => setExplaining(true)}
                                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[0.7rem] font-semibold text-white/60 transition hover:border-white/40 hover:text-white"
                                >
                                  <Info size={12} aria-hidden="true" />
                                  {kinds?.action ?? 'Co to znaczy?'}
                                </button>
                              }
                            />
                          ) : current.kind === 'question' ? (
                            <PillGroup
                              label={current.question.label}
                              choices={current.question.choices}
                              value={answers[current.question.key] ?? null}
                              onChange={(next) => {
                                answerQuestion(current.question.key, next)
                                advance(next)
                              }}
                              columns={current.question.columns}
                              fill
                              sticky
                            />
                          ) : (
                            // THE END OF THE PATH IS A NUMBER.
                            //
                            // Owner: „…ile zdjęć chcesz z tego uzyskać, po tym
                            // pojawia się cena". Every other panel asks something;
                            // this one answers.
                            //
                            // The figure is the WHOLE DAY, not a day rate:
                            // `priceShoot` over the owner's model for a session,
                            // the kind's own rate for a recording. It falls back
                            // to the level's catalogue figure only when neither
                            // model can say — a session with no table, a kind
                            // nobody has priced, or a pricing read that failed.
                            <div className="flex min-h-0 flex-1 flex-col justify-center gap-[clamp(0.3rem,0.9svh,0.6rem)]">
                              <p className="shrink-0 text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-white/55">
                                Cena
                              </p>
                              {/* „Indywidualna wycena" is the table's own answer
                                for the biggest properties, not a failure to
                                find a number — so it is set in the same place
                                the number goes, a size down because it is a
                                sentence. */}
                              <p
                                className={cn(
                                  'shrink-0 leading-none font-bold text-white',
                                  quoted
                                    ? 'text-[length:clamp(1.3rem,3.4svh,1.9rem)] leading-tight'
                                    : 'text-[length:clamp(1.9rem,5.4svh,3rem)]',
                                )}
                              >
                                {quoted
                                  ? 'Wycena indywidualna'
                                  : priced
                                    ? formatPlnMinor(priced.clientNetMinor)
                                    : recordingPrice !== null
                                      ? formatPlnMinor(recordingPrice)
                                      : isRecordingRole
                                        ? // The operator, until the owner sets
                                          // his rates. A figure here would be one
                                          // this page invented — the „500 zł /
                                          // dzień" it used to print belonged to a
                                          // „cały plan" nobody sells now.
                                          'Wycena indywidualna'
                                        : (dayLevel?.price ?? '—')}
                                {/* The suffix belongs to the LEVEL's rate („400 zł
                                  / dzień"), not to a day that has been priced
                                  whole — „2 450 zł / dzień" would read as a
                                  daily rate for a day that is already counted
                                  in. It stays only on the fallback. */}
                                {!quoted &&
                                !priced &&
                                recordingPrice === null &&
                                !isRecordingRole &&
                                dayLevel?.suffix ? (
                                  <span className="text-[length:clamp(0.85rem,1.9svh,1rem)] font-semibold text-white/55">
                                    {' '}
                                    {dayLevel.suffix}
                                  </span>
                                ) : null}
                              </p>
                              <p className="shrink-0 text-[length:clamp(0.8rem,1.8svh,0.9rem)] leading-snug text-white/60">
                                {/* The level the visitor actually chose, when
                                    there was a choice — the operator's „Pół
                                    planu" is not the „Dzień nagraniowy" that
                                    `dayLevel` still finds first in his
                                    catalogue. */}
                                {quoted ? null : (pickedPlan?.name ?? dayLevel?.name ?? null)}
                                {!quoted && (pickedPlan ?? dayLevel) && answered ? ' · ' : null}
                                {answered}
                              </p>
                              <p className={cn(MICRO, 'shrink-0 leading-snug text-white/40')}>
                                {quoted
                                  ? 'Taki zakres wyceniamy indywidualnie — napisz do nas, a odpowiemy z ceną i terminem.'
                                  : priced
                                    ? 'Cena netto za całą sesję, z obróbką wybranych zdjęć. Wybierz termin obok i dodaj do koszyka.'
                                    : recordingPrice !== null
                                      ? 'Cena netto za dzień nagraniowy. Wybierz termin obok i dodaj do koszyka.'
                                      : isRecordingRole
                                        ? 'Dzień nagraniowy wyceniamy indywidualnie — wybierz termin obok, a odezwiemy się z ceną.'
                                        : 'Wybierz termin obok i dodaj do koszyka. Nic nie jest jeszcze zarezerwowane.'}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      /* ── BEFORE ANYTHING IS CHOSEN ───────────────────
                       Owner: *„zamiast szarego ekranu na start niech będzie
                       jakiś napis, infografika «kogo dzisiaj potrzebujesz», albo
                       info żeby najpierw wybrać operatora lub fotografa"*.

                       The panel was empty until a card on the left was pressed —
                       and empty is not neutral here: it is the biggest box on
                       the screen, sitting under a heading that says „Ustal
                       szczegóły" and showing that there are none. Somebody who
                       does not know this page reads that as broken.

                       So it says what the page is: three steps, one sentence
                       each, with the one that is waiting for them marked. Not a
                       control — there is nothing here to press, the answer is a
                       card in the column to the left, and a button that only
                       pointed at one would be a second way to do the same thing.
                    */
                      <div className="flex min-h-0 flex-1 flex-col justify-center gap-[clamp(0.6rem,1.8svh,1.25rem)] text-center">
                        <span
                          aria-hidden="true"
                          className="mx-auto flex h-[clamp(2.5rem,6svh,3.5rem)] w-[clamp(2.5rem,6svh,3.5rem)] items-center justify-center rounded-2xl border border-white/[0.14] bg-white/[0.05]"
                        >
                          <Users size={22} strokeWidth={1.8} className="text-white/70" />
                        </span>

                        <p className="text-[length:clamp(1rem,2.4svh,1.35rem)] leading-tight font-semibold text-white">
                          Kogo dzisiaj potrzebujesz?
                        </p>
                        <p className="mx-auto max-w-[32ch] text-[length:clamp(0.8rem,1.8svh,0.9rem)] leading-snug text-white/55">
                          Zacznij po lewej — wybierz fotografa albo operatora kamery. Resztę
                          ustalimy tutaj, pytanie po pytaniu.
                        </p>

                        <ol className="mx-auto mt-[clamp(0.2rem,0.8svh,0.5rem)] flex w-full max-w-[26rem] flex-col gap-[clamp(0.3rem,0.9svh,0.55rem)] text-left">
                          {[
                            { n: 1, title: 'Kogo potrzebujesz', note: 'fotograf albo operator' },
                            {
                              n: 2,
                              title: 'Ustal szczegóły',
                              note: 'miasto, sesja, plan, zdjęcia',
                            },
                            { n: 3, title: 'Wybierz termin', note: 'dzień i godzina' },
                          ].map((row) => (
                            <li
                              key={row.n}
                              /* Only the first one is lit: it is the step that is
                               actually waiting. Lighting all three would make
                               this a legend rather than an instruction. */
                              className={cn(
                                'flex items-center gap-3 rounded-2xl border px-3 py-[clamp(0.3rem,0.9svh,0.55rem)]',
                                row.n === 1
                                  ? 'border-white/[0.18] bg-white/[0.07]'
                                  : 'border-white/[0.08] bg-white/[0.02]',
                              )}
                            >
                              <span
                                className={cn(
                                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[0.7rem] font-bold',
                                  row.n === 1
                                    ? 'border-white/30 text-white/80'
                                    : 'border-white/12 text-white/35',
                                )}
                              >
                                {row.n}
                              </span>
                              <span className="min-w-0">
                                <span
                                  className={cn(
                                    'block text-[length:clamp(0.82rem,1.8svh,0.9rem)] leading-tight font-semibold',
                                    row.n === 1 ? 'text-white' : 'text-white/45',
                                  )}
                                >
                                  {row.title}
                                </span>
                                <span
                                  className={cn(
                                    'block text-[0.75rem] leading-tight',
                                    row.n === 1 ? 'text-white/55' : 'text-white/30',
                                  )}
                                >
                                  {row.note}
                                </span>
                              </span>
                              {row.n === 1 ? (
                                <ArrowLeft
                                  size={14}
                                  aria-hidden="true"
                                  className="ml-auto shrink-0 text-white/40"
                                />
                              ) : null}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* WHAT IS BEING HIRED IS A PERSON, NOT A PLACE — and it is
                      the LAST thing in this column, not the first.

                      Owner: *„to w innym miejscu, bo psuje harmonię"*. Every
                      other block here is a caption over a control; this one is
                      a sentence, and standing at the top it was the first thing
                      read in a column whose job is to ask. At the foot it is
                      what it always was: a footnote to the answers above it.

                      Shoot roles only. The meeting has a place already —
                      ours. */}
                    {picked?.isShootDay ? (
                      <p className={cn(MICRO, 'flex items-start gap-1.5 text-white/45')}>
                        <Info size={12} aria-hidden="true" className="mt-[0.15em] shrink-0" />
                        <span>
                          <span className="font-semibold text-white/70">
                            Wynajmujesz ekipę, nie miejsce.
                          </span>{' '}
                          Przestrzeń organizujesz Ty, po rezerwacji dajesz znać gdzie.
                        </span>
                      </p>
                    ) : null}
                  </div>
                </StepGate>
              </section>

              {/* ── STEP 3 · SLOT, AND WHAT IT ADDS UP TO ──────── */}
              <section aria-label="Wybór terminu" className="flex min-h-0 flex-col">
                <StepLabel n={3} title="Kiedy?" locked={!detailsDone} />
                <StepGate locked={!detailsDone} hint="Uzupełnij szczegóły w kroku 2">
                  {/* `flex-1`, not `shrink`. Owner: *„bliżej siebie te sekcje,
                  niech kalendarzowa będzie dłuższa w dół"* — the panel used to
                  take its content's height while `mt-auto` pinned the summary
                  to the foot of the column, so the two sat at opposite ends of
                  a 120px hole. The calendar takes the slack now and the summary
                  follows directly under it.

                  `m-auto` on the inner wrapper rather than `justify-center` on
                  this box: both centre the month in a taller panel, but auto
                  margins collapse to zero when the content outgrows the box,
                  where `justify-center` would push the top of the calendar out
                  of reach above the scroll. */}
                  <div className="mt-[clamp(0.45rem,1.6svh,1.25rem)] flex min-h-0 flex-1 overflow-y-auto rounded-3xl border border-white/[0.12] bg-white/[0.04] px-[clamp(0.7rem,1.7svh,1.5rem)] py-[clamp(0.5rem,0.9svh,1.5rem)] backdrop-blur-md">
                    <div className="m-auto w-full">
                      <ConsultationCalendar
                        value={slot}
                        onChange={setSlot}
                        slots={slotChoices}
                        openDays={openDayChoices}
                        // „3 · Kiedy?" sits directly above this panel; „Wybierz
                        // dzień" under it is the same question in a smaller voice.
                        showDayLabel={false}
                      />
                    </div>
                  </div>

                  {/* THE SUMMARY ENDS THE COLUMN, not the page.
                  It used to be a full-width bar under everything, which is
                  where a summary goes on a page you scroll. On a page you read
                  across, the last column is where the path finishes — so the
                  answer and the button that acts on it sit directly under the
                  final question. `mt-auto` pins them to the foot of the screen
                  whatever the calendar's height turns out to be. */}
                  <div className="mt-[clamp(0.45rem,1.6svh,1.25rem)] flex shrink-0 flex-col gap-[clamp(0.3rem,1.1svh,1rem)] rounded-3xl border border-white/[0.14] bg-white/[0.05] px-[clamp(0.7rem,1.7svh,1.5rem)] py-[clamp(0.55rem,1.2svh,1.5rem)] backdrop-blur-md">
                    <div className="min-w-0">
                      <p className="text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-white/55">
                        Twój wybór
                      </p>
                      <p className="mt-1.5 text-[length:clamp(0.95rem,2.1svh,1.05rem)] leading-snug font-semibold text-white">
                        {picked ? picked.name : 'Wybierz specjalistę'}
                        {/* A level this summary can honestly name. With one
                            on offer there is nothing to choose and `dayLevel`
                            IS the answer; with two, naming the first one the
                            catalogue happens to list would tell somebody they
                            had picked „Pół planu" before they picked
                            anything. */}
                        {asksPlan
                          ? pickedPlan
                            ? ` · ${pickedPlan.name}`
                            : null
                          : dayLevel
                            ? ` · ${dayLevel.name}`
                            : null}
                        {/* The label, not the slug: `3-plus` is what the link
                        carries and "3+" is what a person reads. */}
                        {asksSessionType && pickedSession ? ` · ${pickedSession.label}` : null}
                        {answered ? ` · ${answered}` : null}
                        {isOnline ? ' · zdalnie' : pickedCity ? ` · ${pickedCity.name}` : null}
                        {slot
                          ? ` · ${slotFormatter.format(new Date(`${slot.date}T00:00:00`))}, ${slot.time}`
                          : null}
                      </p>
                      <p className={cn(MICRO, 'mt-1 text-white/55')}>
                        Nic nie jest zarezerwowane, termin potwierdzamy mailem.
                      </p>
                    </div>

                    {orderHref ? (
                      <Link
                        href={orderHref}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[image:var(--flou-gradient-solid)] px-6 py-[clamp(0.55rem,1.4svh,0.75rem)] text-center text-[0.95rem] font-semibold text-white transition-transform duration-300 hover:scale-[1.04]"
                      >
                        Dodaj do koszyka
                        <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                    ) : (
                      <span className="rounded-full bg-white/10 px-6 py-[clamp(0.55rem,1.4svh,0.75rem)] text-center text-[0.95rem] font-semibold text-white/60">
                        Najpierw wybierz specjalistę
                      </span>
                    )}
                  </div>
                </StepGate>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StepLabel({ n, title, locked }: { n: number; title: string; locked?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full border text-[0.75rem] font-bold transition-colors duration-500',
          locked ? 'border-white/10 text-white/30' : 'border-white/20 text-white/70',
        )}
      >
        {n}
      </span>
      {/* The HEADING stays legible while the step under it is blurred. A
        number and a title a reader can still read is a step waiting its turn;
        blur the label too and the column reads as a page that failed to
        load. */}
      <h2
        className={cn(
          SUBHEADING,
          'transition-colors duration-500',
          locked ? 'text-white/40' : 'text-white',
        )}
      >
        {title}
      </h2>
    </div>
  )
}

/**
 * A step that is not the reader's turn yet.
 *
 * Owner's flow, in his words: *„najpierw klient musi wybrać kogo potrzebuje,
 * na punkty 2 i 3 jest blur, gdy wybierze punkt 2 się odbluruje, gdy przeklika
 * wszystko to odbluruje się punkt 3"*. Three columns side by side show the
 * whole path at once, which is the point of the layout — and also why somebody
 * landing on it could start in the middle and answer questions about a
 * specialist they had not chosen. The blur makes the order visible without
 * taking the shape of the page away: the reader still sees that two more steps
 * exist and roughly how big they are.
 *
 * ── WHY A VEIL AND NOT A DISABLED PANEL ──────────────────────────────────
 * Blur alone is decoration — a mouse still clicks through it and a screen
 * reader never sees it at all, so a locked column would stay fully operable
 * for exactly the people least able to tell it was meant to be locked. So the
 * content is `inert` and `aria-hidden` while it waits, and the caption that
 * replaces it in the accessibility tree says what to do instead. That caption
 * is also what stops the blur from reading as a broken page to everyone else:
 * a frosted panel with no explanation is a bug, the same panel with „najpierw
 * wybierz…" is an instruction.
 *
 * The transition runs on `filter` and `opacity` only. Height stays put: the
 * columns are laid out by the grid, so nothing moves when a step unlocks and
 * the eye can stay where the answer was.
 */
function StepGate({
  locked,
  hint,
  children,
}: {
  locked: boolean
  hint: string
  children: React.ReactNode
}) {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        aria-hidden={locked || undefined}
        inert={locked || undefined}
        className={cn(
          'flex min-h-0 flex-1 flex-col transition-[filter,opacity] duration-500 ease-out',
          locked && 'pointer-events-none opacity-45 blur-[7px] select-none',
        )}
      >
        {children}
      </div>

      {locked ? (
        <p
          className={cn(
            MICRO,
            RADIUS.pill,
            // Near the top of the column rather than dead centre: that is where
            // the first question would be, so the instruction lands where the
            // eye is already looking.
            'absolute inset-x-0 top-[clamp(1.5rem,14%,5rem)] mx-auto inline-flex w-fit max-w-[90%] items-center gap-2 border border-white/15 bg-black/70 px-4 py-2.5 text-center font-semibold text-white/75 backdrop-blur-sm',
          )}
        >
          <Lock size={13} aria-hidden="true" className="shrink-0 text-white/45" />
          {hint}
        </p>
      ) : null}
    </div>
  )
}
